/**
 * 数据层 — 类型定义
 * 所有游戏数据的接口/类型定义都集中在这里，是游戏架构的基石。
 * 遵循策划文档《女明星模拟器 · 完整玩法机制文档》。
 */
import type { AvatarConfig } from '../avatar/config';
import type { SceneKey } from './SceneBgs';

/** 显示属性（0-100） */
export interface AttributeSet {
    /** 颜值 */
    charm: number;
    /** 演技 */
    talent: number;
    /** 气质 */
    temperament: number;
    /** 体能 */
    stamina: number;
    /** 情商 */
    eq: number;
    /** 智慧：复杂角色理解、高智/专业类角色的气质贴合，影响试镜判定 */
    wisdom: number;
    /** 才艺：歌舞乐器类特殊角色试镜判定（影视剧/综艺通用） */
    artistry: number;
    /** 口碑 */
    reputation: number;
}

export type AttributeKey = keyof AttributeSet;

/**
     * 属性元数据：名称、上下限、初始范围。
     * ⚠️ `min` 表内按 **100 制**书写，装载时与 max/startRange 一起 ×ATTR_SCALE（`GameData.applyAttributeScale`，
     *    2026-10-04 补齐 min 那一行——此前进过本表的 min 全是 0，不乘也看不出来）。
     * ⚠️ 全库只有口碑把 min 写成负数（-100 ⇒ 运行时 -500，见 attributes.ts）；其余七维照旧 0 起 ⇒
     *    读「属性下限」时⛔ 当常量 0 处理（如 `Math.max(0, v)` 这类兜底会把负口碑抹平）。
 */
export interface AttributeDef {
    key: AttributeKey;
    name: string;
    min: number;
    max: number;
    /** 开局随机范围 [min, max] */
    startRange: [number, number];
}

/** 两项显示资源 */
export interface ResourceSet {
    /** 体力：行动消耗资源，独立数值，有上限 */
    energy: number;
    /** 金钱：行动消耗资源，通告获得 */
    money: number;
}

/** 玩家运行时状态 */
export interface PlayerState {
    name: string;             // 主角全名（= 姓氏 + 名字，文案替换/展示统一读这里）
    /** 姓氏（1~2 个汉字；建角页与名字分开填写，2026-10-19） */
    surname?: string;
    /** 名字（1~2 个汉字；建角页与姓氏分开填写，`name` 由两者拼出，展示侧只读 `name`） */
    givenName?: string;
    /** 家人/助理对主角的称呼（开局自定昵称，如「小花」「姐」；小助理用「{nickname}姐」称呼） */
    nickname: string;
    /**
     * 开局身份（`TagDef.pool==='origin'` 的 id，2026-10-19 开局身份拆分）。
     * 身份不写进 `tags`、不占天赋槽；旧档缺失时由 `migratePlayer` 按 TAG_001/012/013 反推。
     */
    origin?: string;
    /** 素人身份「每月首次训练额外 +1 路径进度」的已触发月份（year*12+month；0=本月未触发） */
    originTrainingMonth?: number;
    /** 主角捏脸配置（67 批开局确认时写入；旧档无此字段 = 显示回落 emoji） */
    avatar?: AvatarConfig;
    age: number;             // 当前年龄（22起步）
    year: number;            // 当前年份
    month: number;           // 当前月份 1-12
    week: number;            // 当前周 1-4
    /** 主角性别（预留：为后续开局性别自选；当前建角固定 'female'） */
    gender: 'male' | 'female';
    attributes: AttributeSet;
    resources: ResourceSet;
    /** 名气：隐藏数值，仅用于映射咖位等级 */
    fame: number;
    /**
     * 微博粉丝数（剧宣批 v11 落盘字段）：锚点 = `fame × FANS_ANCHOR_K × fanCharisma`；
     * 涨名气当场补 `FANS_IMMEDIATE_RATE`（带抖动）的缺口，月末再朝锚点收敛+绕锚扰动。
     * ⛔ 读取端不得由 fame 现算——随机只在「涨粉当场」与「月末结算」两处 roll 且立即落盘，刷新不许跳。
     */
    fans: number;
    /**
     * 个体号召力系数（v11 追加）：开局 roll 一次 `FANS_CHARISMA_RANGE` 定档、终身不变。
     * 用途=打散「粉丝=名气×2000」的整数台阶（同名气不同人的粉丝盘差 ±35%）；
     * ⛔ 每月重摇（会让粉丝凭空跳变），⛔ 对外解释（纯数值层，不进任何文案）。
     * 旧存档缺失时载入补 roll（可补字段，⛔ 因此升 SAVE_VERSION）。
     */
    fanCharisma: number;
    /** 咖位等级 1-10 */
    rank: number;
    /**
     * 各训练路径累计次数（key=AttributeKey 可训练路径，口碑不计）：
     * 该路径所有档位训练历史执行总次数（升档替换不清零、只增不减、跨档累加）。
     * 训练升级（12/30 次）、特殊训练/工作解锁（requirePathCounts）都以此判定。
     */
    pathCourseCounts: Record<AttributeKey, number>;
    /** 当前拥有的标签 ID 列表 */
    tags: string[];
    /** 剧情/进度标记（固定事件/主线剧情后续使用） */
    flags: Record<string, boolean>;
    /** 剧情收件箱（PlotSystem 推送的待处理剧情：message 对话 / media 讨论贴 / encounter 偶遇）。
     *  可选字段、缺省=空 ⛔ 升 SAVE_VERSION（先例 heatPromoted）。 */
    plotMessages?: PlotMessage[];
    /** 剧情聊天消息存根（2026-10-14 茄子定「message 分类的剧情都是人脉页发的聊天消息」）：
     *  message 拍演出时 VN 档不弹卡，把文案转投到这里，聊天页回放、行级红点提示未读。
     *  可选字段、缺省=空 ⛔ 升 SAVE_VERSION（先例 plotMessages）。 */
    plotChats?: PlotChat[];
    /** 特殊剧情给剧加的热度 { scriptId: ΣΔheat }（2026-10-10：剧情热度是世界事实，⛔ 不走剧宣账本、无封顶）。
     *  可选字段、缺省=空 ⛔ 升 SAVE_VERSION；FanSystem.workHeatOf 叠加读取。 */
    plotHeatBonus?: Record<string, number>;
    /** 签约通告队列（最多3个） */
    contracts: Contract[];
    /** 试镜成功待签约的角色列表（名额满时挂起，剧本换新前有效） */
    auditionQualified: AuditionQualified[];
    /** 5c 竞角结果（`scriptId#roleIndex` → 是否竞得）。
     *  竞角结果一旦掷出即落库，⛔ 随玩家反复进出揭晓页重摇——旧实现把结果只存在面板内存里，
     *  同一份状态里再点一次 5c 就是一套新骰子（2026-10-10 架构审查「形状 A 的反向变体」）。
     *  生命周期跟 auditionQualified 一致：换新修剪、收口清空、批量重排三处一并作废。
     *  可选字段、缺省=没掷过 ⇒ ⛔ 升 SAVE_VERSION（先例 items／heatPromoted／plotMessages）。 */
    auditionReveal?: Record<string, boolean>;
    /** 月末结转包（2026-11-03 竞角重排批）：竞角队列与收口夜剧单跨这次存盘。
     *  读档后队列原样再弹、成败不重掷（掷出的结果记在 auditionReveal 里，跟着这包一起存）。
     *  可选字段、缺省=没结转；老档读回来即「无队列」，不升 SAVE_VERSION（老档本来就没有队列）。 */
    monthHandover?: MonthHandover;
    /** 普通 NPC 列表（开局生成，跨季度常驻，咖位随接戏成长） */
    normalNpcs: NormalNpc[];
    /** NPC好感度 { NPC_ID: value }（特殊 NPC + 普通 NPC 统一承载） */
    relationships: Record<string, number>;
    /** 熟遇增益记账 { npcId: 绝对月份(year*12+month) }：同一 NPC 每月片场熟遇好感增益限 1 次（旧档缺省视为空） */
    encounterMetMonth?: Record<string, number>;
    /** 下月4周行程安排 [4个 ScheduleSlot] */
    schedule: (ScheduleSlot | null)[];
    /** 本月社交次数统计 { chat: number, invite: number } */
    socialCounts: { chat: number; invite: number };
    /**
     * 背包（物品 id → 持有数）：偶遇「送礼」那一格扣这里，⛔ 花钱（用户 2026-10-10 定：
     *  送的是背包里已有的礼物，⛔ 是当场买）。可选字段、缺省=空 ⇒ ⛔ 升 SAVE_VERSION
     *  （先例 heatPromoted/encounterMetMonth）；老档载入补一份开局测试礼物，见 `GameState.migratePlayer`。
     *  ⚠️ 礼物来源（朋友回赠/商店）是后续批次，当前唯一进货渠道就是那份补充（`Items.ts` STARTER_GIFTS）。
     */
    items?: Record<string, number>;
    /** 最后营业月份（绝对月份），null=从未营业 */
    lastPostMonth: number | null;
    /** 近3个月发布记录（滚动窗口），任意月份≥1条即无脱粉惩罚 */
    postRecords: PostRecord[];
    /** 剧本池当前上架剧本 ID 列表 */
    marketScripts: string[];
    /** 本生当前刷新周期已展示的剧本ID（防重复；换新时清空） */
    scriptShown: string[];
    /**
     * 剧本池模式：固定排期（scheduled，默认）/ 随机池（random，通关一周目解锁）
     * 每季度按排期表固定上架指定剧本；随机池为现有随机刷新机制
     */
    marketMode: 'scheduled' | 'random';
    /**
     * 经纪人月度通知历史（页面 5b 回看）。⚠️ 74 批改判后 `NoticeSystem` **⛔ 再往这里写**
     * （同一句话小助理已当面口播，⛔ 两边各挂一份）⇒ 新档恒为空数组，字段与页面渲染分支保留给旧档回看。
     */
    managerNotices: ManagerNotice[];
    /** 上次已读的经纪人通知条数（未读提示用：managerNotices.length > lastReadNoticeCount 时有未读） */
    lastReadNoticeCount: number;
    /** NPC 主动打招呼队列（月初产出，全局每月最多 GREETING_MONTHLY_MAX 条；跨月保留，展示按 NPC 过滤） */
    greetings: NpcGreeting[];
    /** 小助理播报消息列表（最后 50 条持久化：对话框可回放，未读 = 总数 − 已读） */
    assistantMessages: AssistantMessage[];
    /** 小助理已播报条数（对话框消费逐条 +1；悬浮按钮未读角标 = assistantMessages.length − 该值） */
    assistantReadCount: number;
    /** 累计赚取金钱（财富成就用） */
    totalEarnedMoney: number;
    /** 累计完成通告数（通告成就用） */
    totalShotsCompleted: number;
    /** 累计发帖条数（lifetime；结局批 2026-09-29 新增，纯统计可补字段，缺省 0，⛔ 不因此升存档版本） */
    totalPosts?: number;
    /**
     * 特训累计完成次数 { scheduleId: 次数 }（特训重做批 2026-10-30）：同一特训累计 10 次
     * 授对应 TAG_SKILL_* 能力 tag（结算落 ScheduleSystem.settleSpecialTraining）。
     * 可补字段、缺省 0 起算——老档不迁移旧特训进度（特训次数此前无价值），⛔ 不因此升存档版本。
     */
    specialTrainingCounts?: Record<string, number>;
    /** 累计获赞数（lifetime，发布落盘时累加；结局批 2026-09-29 新增，同上不升版） */
    totalLikes?: number;
    /**
     * 本周目解锁的成就 ID（结局批 2026-09-30：checkMonthly 解锁时同步 push，开局清空；
     * 结算页「周目结算」按此全量展示——⛔ 用终局补判那一次的返回值，平时月度解锁会漏）。
     * 旧档缺省 undefined → 终局兜底退化为补判那一次，⛔ 不升存档版本。
     */
    runAchievements?: string[];
    /**
     * 网友评价「同月固定一批」缓存（批 C 功能先行 2026-09-30 用户二次拍板：原「同月不重复」作废——
     * 同月内重开面板恒为同一批，下个月第一次点开才重新抽）。month=绝对月；
     * 旧档缺省 undefined，⛔ 不升存档版本。
     */
    reviewCache?: { month: number; items: { speaker: string; text: string }[] };
    /** 累计接受好友客串邀约次数（成就批 2026-09-29 新增，GuestCameoSystem.acceptEffects 落盘；
     *  纯统计可补字段、缺省 0，⛔ 不因此升存档版本——先例 totalPosts/totalLikes） */
    totalGuestRides?: number;
    /**
     * 买通稿·本周目累计已买次数（经纪人工作台批 2026-10-27）：反噬概率随它递增（越买越容易被扒）。
     * 可补字段、缺省 0，⛔ 不升存档版本。
     */
    promoTotal?: number;
    /**
     * 买通稿·本月已买记录（同批）：abs=绝对月份做月界重置，count=本月已买次数；
     * 读侧 abs 不匹配即视为 0。可补字段、缺省 undefined，⛔ 不升存档版本。
     */
    promoThisMonth?: { abs: number; count: number };
    // ===== 商务代言（2026-10-30 批；可补字段缺省，⛔ 不因此升存档版本——本批版本升级由片酬基准表重铺承担） =====
    /** 在约商务长约（挚友单次即结不落此表；月结/解约判定走 EndorsementSystem.monthlySettle） */
    endorsements?: EndorsementContract[];
    /** 待签品牌邀约（至多 1 个；送达链=经纪人 VN+签约弹窗·重档，两键强制二选一） */
    pendingBrandOffer?: PendingBrandOffer | null;
    /**
     * 放弃过的品牌（5 年拉黑窗口，2026-10-30 茄子令「放弃永久拉黑改成五年内」）：
     * abs=放弃时的绝对月，60 个月内不重复递，窗口过自动解禁。旧档 string[] 由 migratePlayer
     * 折算成 { id, abs: 读档时点 }（不升 SAVE_VERSION，先例 totalPosts）。
     */
    brandRejected?: { id: string; abs: number }[];
    /**
     * 结束合作品牌的再递约冷却（2026-11-03 茄子令）：brandId → 锁到的绝对月（含当月），到期自动解禁。
     * 解约（品牌方终止/玩家主动解约/挚友违约）=24 个月、约满自然到期或放弃续约=12 个月；
     * 玩家放弃「新邀约」仍走 brandRejected 的 5 年窗口，两者语义不同。
     * 可补字段、缺省=无冷却，⛔ 不升 SAVE_VERSION（先例 varietyShowCooldowns）。
     */
    brandCooldowns?: Record<string, number>;
    /** 待弹官宣（签约当刻入队、确认行程时弹；可补字段、缺省=空，⛔ 不升存档版本） */
    pendingAnnounces?: PendingBrandAnnounce[];
    /** 累计签约商务次数（含挚友单次；实绩面板代言分区用） */
    endorseTotal?: number;
    /** 累计商务收入（实绩用；挚友单次与长约头款/尾款都计入） */
    endorseEarned?: number;
    // ===== 宣传综艺（2026-10-31 批；可补字段缺省，⛔ 不升存档版本——先例 pendingBrandOffer/heatPromoted） =====
    /** 待签宣传综艺邀约（至多 1 个；一番杀青掷骰递约，送达链同商务=经纪人 VN+重档弹窗两键） */
    pendingVarietyOffer?: PendingVarietyOffer | null;
    /** 已签的综艺录制通告（占自由日周同尺互斥；到周自动赴约 VN、录制月月结自动结算） */
    varietyGuests?: VarietyGuestBooking[];
    /**
     * 同节目冷却（2026-10-31 茄子令「现实不会一年上同一综艺两次」）：showId → 冷却到期绝对月
     * （录制月结盖章=录制月+N 个月；到期前该节目不递，放弃邀约不盖章）。可选字段、缺省=无冷却，
     * ⛔ 不升 SAVE_VERSION（先例 heatPromoted）。
     */
    varietyShowCooldowns?: Record<string, number>;
    /** 作品履历（批6）：播出完结时写入，杀青未播完的不在此；旧档/新开局缺省视为空 */
    filmography?: FilmographyEntry[];
    /** 历届奖项结算账本（每届 4 条）：往屆回看 + 同奖复获递减的计数来源 */
    awardHistory?: AwardRecord[];
    /** 待颁奖名单（12 月末写入，1 月颁奖后清空）；null = 当前无在途提名 */
    pendingAward?: PendingCeremony | null;
    /** 已展示过的颁奖夜 key（`awardId-edition`），供 UI 去重弹出（批 B 使用） */
    ceremonySeen?: string[];
    /**
     * 剧宣批：本玩家各剧本的累计宣传热度 { scriptId: ΣΔheat }（⛔ 撞顶复用 scriptId 会串账——
     * 18 年排期一剧一签，同剧重签不存在，可接受）。剧的客观热度 workHeat = script.heat + 本值。
     */
    heatPromoted?: Record<string, number>;
    /**
     * 好友客串批②：各剧本客串累计抬的热度 { scriptId: ΣΔheat }（⛔ 与 heatPromoted 共用账本/封顶，
     * 用户口令各刷各的）。workHeat = min(底数+剧宣Σ, 底数×2) + min(本Σ, 底数×1)。
     * 可选字段、缺省=空 ⛔ 升 SAVE_VERSION（先例 heatPromoted/encounterMetMonth）。
     */
    guestPromoted?: Record<string, number>;
    /**
     * 好友客串批③：各剧本已客串过的好友名单 { scriptId: npcId[] }——同一部剧每个好友只请得动一次
     * （一剧一签，二次开口=赖皮）。⛔ 与 guestPromoted 混用（本表记「谁」，那表记「热度Σ」）。
     * 可选、缺省=空 ⛔ 升 SAVE_VERSION。
     */
    guestedBy?: Record<string, string[]>;
    /**
     * 好友剧宣批（2026-10-28）：各剧本好友站台累计抬的热度 { scriptId: ΣΔheat }。
     * ⛔ 与 guestPromoted 分账但**共用** GUEST_HEAT_CAP_MULT 封顶池（宣传=发条微博的小头，客串=来演戏的大头，
     * 两条通道合计不得刷穿底数×1）。可选字段、缺省=空 ⛔ 升 SAVE_VERSION（先例 guestPromoted）。
     */
    friendPromoted?: Record<string, number>;
    /**
     * 好友剧宣批：各剧本已请来站台的好友名单 { scriptId: npcId[] }——同一部剧每位好友只请一次
     * （口径同 guestedBy 一剧一签）。⛔ 与 friendPromoted 混用（本表记「谁」，那表记「热度Σ」）。
     * 可选、缺省=空 ⛔ 升 SAVE_VERSION。
     */
    promoAskedBy?: Record<string, string[]>;
    /**
     * 全网影视热度榜（heatchart 批 2026-09-30）：当月定格快照。publishDueChart 在每月决策日入场
     * 写一次（seenAbs 幂等门），shownAbs 记弹窗展示到过的月份（关榜前重入决策页⛔ 重弹）。
     * 可选、缺省=空 ⛔ 升 SAVE_VERSION（先例 heatPromoted/totalPosts）。
     */
    heatChart?: HeatChartState;
    /**
     * 头像部件所有权（自由出行批 2026-10-11，设计=docs/自由出行与小地图-设计方案.md §四.5）：
     * key=AvatarConfig 的部件维度，value=已购下标列表；拥有是永久的（换回旧款免费）。
     * 购买=价签付款 → 加入本表 + 立即上身（写 player.avatar）；衣帽间只读本表（只换已拥有）。
     * ⚠️ 按 parts 下标记——⛔ 插入/删除/调序会错位（见头像部件系统 §五）。
     * 老档缺省 = 按 player.avatar ?? DEFAULT_CONFIG 补初始拥有件（migratePlayer），⛔ 升 SAVE_VERSION。
     */
    owned?: OwnedParts;
    /**
     * 挂起之约（多槽批 2026-10-29，原单槽 `pendingInvite`）：人脉页邀约成功（对方答应）后 **append 进本数组**，
     * **数组顺序 = 挂约顺序**；**周级互斥（2026-10-29 茄子令）**：一周只能安排一个自由日行程
     * （不分邀约/飞行/未来行程），多槽只用于分布到不同周；选周时已占用的周不可选。
     * 玩家在该 NPC 聊天页选定本月第几周（`week` 1~4）；到选定周的周末自动演出赴约 VN 结算 +5。
     * `week` 未定=约未成形：不自动赴约（周末照常出行），整月未决到收月作废、月限不退。
     * ⛔ 同一 NPC 同时只挂一个（2026-10-29 茄子定「加闸」）；不同 NPC 可挂在不同周。
     * 可选、缺省=空 ⛔ 升 SAVE_VERSION；前身单槽 `pendingInvite` 由 migratePlayer 折进本数组（不升版本）。
     */
    pendingInvites?: { npcId: string; week?: number }[];
    /**
     * 聊天交换的定格回放账（2026-10-28 修「读档后记录消失」家族）：人脉页六类交换（聊天/送礼/邀客串/
     * 邀宣传/招呼回应/邀约）的气泡当场定格记账（⛔ 不落盘，存档模型统一 2026-10-28：随周收口捎带落盘，
     * 周内退出=重玩本周、整笔交换回滚）——读档/换人重建聊天记录（`rebuildChatLog`）时按 abs
     * 与招呼/剧情私信统一排序回放，定格不重掷。`aff`=存档兼容字段（2026-11-01 起不再渲染，
     * 好感变化改走聊天页内 `flashChatHint` 临时气泡；旧档里的值读出后忽略）。
     * 可选、缺省=空 ⛔ 升 SAVE_VERSION（先例 pendingInvite；前身 `inviteMsgs` 由 migratePlayer 折进本账）。
     */
    chatMsgs?: { npcId: string; me: boolean; text: string; abs: number; aff?: number }[];
    /**
     * 探班记账 { npcId: 绝对月份(year*12+month) }：影视城拜访的最近一次月份。
     * 月闸读它（2026-10-12 茄子令「一个月探班同一个人最多一次」，`VISIT_MONTHLY_LIMIT`=1）：
     * 相等即本月已探过，跨月自动恢复。旋钮若调回 0，本表退回纯记账形态。
     * 可选、缺省=空 ⛔ 升 SAVE_VERSION（先例 encounterMetMonth）。
     */
    visitMonth?: Record<string, number>;
}

/**
 * 头像部件所有权（按维度记已购下标）。五类店共 14 个可购维度：
 * 露米娜发艺=hairFront/hairSides/hairBack/hairColor；缪斯服饰=cloth；星辉珠宝=acc/glasses；
 * 蒂尔玛医疗美容=face/brow/eye/nose/mouth/skin/eyeColor（五官/肤色/瞳色唯一窗口）。
 */
export interface OwnedParts {
    face?: number[];
    skin?: number[];
    hairFront?: number[];
    hairSides?: number[];
    hairBack?: number[];
    hairColor?: number[];
    brow?: number[];
    eye?: number[];
    eyeColor?: number[];
    nose?: number[];
    mouth?: number[];
    cloth?: number[];
    glasses?: number[];
    acc?: number[];
}

/**
 * 热度榜单行快照（heatchart 批 2026-09-30）。榜单是「当下市场」：在播期内的活账，
 * 与履历/评选的「播完定格快照」哲学相反，剧名/热度都读现表（下架剧⛔ 进榜，不存在回改漂移面）。
 */
export interface HeatChartRow {
    scriptId: string;
    /** 排名：TOP5 行 = 1-5；榜外玩家专属提示行 = 真实名次（outOfTop） */
    rank: number;
    genre: ScriptGenre;
    /** 当前热度 = FanSystem.workHeatOf（含剧宣/客串累计封顶），榜单与评选、履历同一个数 */
    heat: number;
    /** heat ≥ GameConfig.HEAT_BOOM_LINE，渲染「爆」标 */
    boom: boolean;
    /** 主演演员名（番位序：男主角/女主角位全上，不足 2 人按番位补配角；解析不到的不占位） */
    stars: string[];
    /** 玩家本人在这部剧的在播位里（行高亮 + 播报口径用） */
    mine: boolean;
    /** 上月排名（0 = 上月不在榜），渲染 ↑n / ↓n / 新上榜 */
    prevRank: number;
    /** true = 榜外玩家专属提示行（真实名次，⛔ 计入 TOP5） */
    outOfTop?: boolean;
}

/** 热度榜月度定格（PlayerState.heatChart）：见字段注释 */
export interface HeatChartState {
    /** 已定格的绝对月份（year*12+month；幂等门：同月重入⛔ 重算、prev⛔ 重复漂移） */
    seenAbs: number;
    /** 弹窗已展示的绝对月份（0 = 本月榜还没给玩家看过；unseenChart 判据） */
    shownAbs: number;
    /** TOP5 + 榜外玩家行（按排名序） */
    rows: HeatChartRow[];
}

/** 跨周目商店购买状态（按原型：开局咖位 / 开局属性加成 / 额外特性点） */
export interface ShopPurchases {
    /** 开局咖位已购买等级（0-4，最终 Lv.1+rank） */
    rank: number;
    /** 开局属性加成：各属性已购买等级（0-10，+5/级，每项上限 +50） */
    attrs: Record<AttributeKey, number>;
    /**
     * 额外特性点已购买数量（0-2，开局特性点数 = 基础预算 + points）。
     * 2026-10-21 特性点数制：旧字段名 `slots`（额外天赋槽购买数）1:1 折成本值，
     * 折算在 `SaveSystem.migrateShopPurchases` 里做（旧 meta 无本字段时读 slots）。
     */
    points: number;
}

/** 跨周目继承数据（成就点/解锁成就/商店购买/商店开关） */
export interface MetaData {
    achievementPoints: number;
    unlockedAchievements: string[];
    shopPurchases: ShopPurchases[];
    /** 商店开关：完成过任意周目结局（stage_fail / retired）后为 true */
    shopUnlocked: boolean;
    /** 随机池模式解锁：仅活到 40 岁正常退役才解锁（通关一周目奖励） */
    randomPoolUnlocked: boolean;
    /**
     * 最近一局的周目结算快照（结局批 2026-09-29）：endGame 时写入，GameOverPanel 只读此处
     * （⛔ 读活内存——删档/刷新安全）。MetaData 与玩家存档分离，删档不删它；P2 回顾室的地基。
     * 可补可选字段：旧 meta 缺省 = 结算页回落现读内存（仅兜旧流程残留），⛔ 不因此升存档版本。
     */
    lastCareerSummary?: CareerSummary;
}

/** 存档数据 */
export interface SaveData {
    version: number;        // 存档版本号
    timestamp: number;      // 保存时间戳
    player: PlayerState;
    currentStage: number;   // 当前阶段
    stageStartAge: number;  // 阶段起始年龄
}

/** 剧本类型（内容分类，用于 UI 标签/成就/事件） */
export type ScriptGenre =
    | 'movie'    // 电影
    | 'tv'       // 电视剧
    | 'variety'  // 综艺
    | 'web'      // 网剧/网络短片
    | 'short'    // 短剧
    | 'other';   // 其他

/**
 * 剧本题材标签（heat 公式用，系数见 GameConfig.THEME_FACTORS）
 * 数组第一个为主题材，其余为副题材（对 heat 做微调）。
 */
export enum ScriptTheme {
    Ancient = '古装',
    War = '战争',
    Spy = '谍战',
    Romance = '爱情',
    Idol = '偶像',
    Mystery = '悬疑',
    Urban = '都市',
    Xianxia = '仙侠',
    Fantasy = '奇幻',
    History = '历史',
    Period = '年代',
    LifeStyle = '生活',
    Crime = '犯罪',
    Court = '宫廷',
    MartialArts = '武侠',
    Action = '动作',
    Horror = '恐怖',
    Scheme = '权谋',
    Female = '女性',
    Occupation = '职业',
    Comedy = '喜剧',
    SeriousDrama = '正剧',
    Campus = '校园',
    Music = '音乐',
    Performance = '表演',
    Culture = '文化',
    SciFi = '科幻',
    Disaster = '灾难',
    Military = '军旅',
}

/** 剧本角色（V1 每本剧本可有多角色，玩家可试镜任一角色） */
export interface ScriptRoleDef {
    /** 角色名；综艺等无姓名角色可空（UI 只显示角色定位/称谓） */
    name?: string;
    /** 角色定位/称谓，如 女主角 / 女二号 / 飞行嘉宾 / 常驻MC */
    roleType: string;
    /**
     * 角色权重档位（本项目内部字段，**不等同现实娱乐圈的「番位」排序**）：
     * 1=一番、2=二番、3=三番、4=四番、5=五番，映射为权重系数
     * （1.0 / 0.7 / 0.5 / 0.2 / 0.1，见 GameConfig.FAME_BILLING_FACTORS），
     * 用于名气加成与评分加权——番位越靠前权重越大。
     * 综艺等无番位概念的剧本可缺省（缺省走 GameConfig.FAME_FACTOR_NO_BILLING = 0.5）。
     * 说明：此「番位」仅作权重档位编码，不承载现实娱乐圈番位语义
     * （平番 / 特出 / 友情出演 / 客串等现实概念不在此表达）；角色卡 UI 不展示番位，只显示 roleType。
     */
    billing?: number;
    /** 角色简介（给玩家的文本，展示试镜目标角色） */
    intro: string;
    /** 角色等级 1-10（与咖位等级同构，角色等级=咖位要求） */
    level: number;
    /** 角色性别限制：'male' 仅男角 / 'female' 仅女角 / 'any' 不限（综艺嘉宾等） */
    gender: 'male' | 'female' | 'any';
    /** 试镜门槛（角色级，纯门槛判定：全部满足才通过；结算时也用作评分基准） */
    requirements: { attr: AttributeKey; value: number }[];
    /**
     * 能力 tag 门槛（特训重做批 2026-10-30，可选）：角色要求持有对应 TAG_SKILL_* 才能试镜——
     * 属性达标但缺 tag = 不可试（判定落 ScriptSystem.isRoleAuditionable 与 audition 双入口）。
     * 消费端首批=综艺主持类角色挂 TAG_SKILL_HOST（下一批综艺导码接入）；本批不给任何角色卡配。
     */
    requiredTag?: string;
    /**
     * 拍摄周期（周）：消费点有二（2026-09-28 统一档期批）——
     * ① 仅最小番位角色（一番；群像剧即并列主角档）的值派生**剧级档期** = ⌈该值 ÷ 4⌉ 个月，
     *    全剧组（玩家合同要排的拍摄周 + NPC 在途工作）统一按此拍摄，同剧同杀青同开播；
     * ② 每个角色自己的值 = 该角色合同要排的拍摄周数（remainingWeeks 初值）。
     * 配角不再因戏份短提前杀青；玩家违约线走剧级档期（scriptScheduleMonths = 一番周期 ×1.5），与角色级周数无关。
     */
    shootWeeks: number;
    /** 属性成长（角色级）：杀青结算时获得的属性成长，可以包含负面值 */
    attributeGrowth: Partial<AttributeSet>;
    /**
     * 片酬（2026-09-26 起等级派生）：**缺省不填**，结算/违约金/带资一律走 `ScriptSystem.rewardOf(role)`
     * = 本字段 ?? GameConfig.PAY_BY_ROLE_LEVEL[等级]（台账 §2 基准表的代码镜像）。
     * 仅「有叙事理由的破格」才显式填写（低成本片酬寒酸/大制作厚道/特殊剧本定制），
     * 且必须行内注释写明缘由 + 在 docs/剧本/导入台账.md override 清单登记一条；常规剧本 ⛔ 不填。
     */
    reward?: number;
}

/** 剧本定义 */
export interface ScriptDef {
    id: string;               // SCRIPT_001
    name: string;
    /** 剧本类型（内容分类） */
    genre: ScriptGenre;
    /** 剧本简介（试镜弹窗/剧本池展示文案） */
    synopsis: string;
    /** 可试镜角色列表（一剧多角色：女一号/男一号/女二号…，角色各自有等级/性别/门槛） */
    roles: ScriptRoleDef[];
    /**
     * 已定阵容（不由玩家试镜的固定演员位；2026-10-21 起参与热度榜主演与年度奖项）。
     * `name` 是剧中角色名，`npcId` 才是演员（NPCS 主表）；缺 npcId 时演员名回退成 name。
     * 奖项档位/性别从 `roleType` 派生（如 女主角 / 女配角），参与评奖的固定阵容必须填。
     * 全库唯一读取口 = data/Cast.fixedCastCredits，别处再遍历 cast 自己拼即第二把尺。
     */
    cast?: {
        name: string;
        billing: number;
        npcId?: string;
        roleType?: string;
    }[];
    /** 关联 NPC ID */
    relatedNPCs: string[];
    /**
     * 题材标签数组：第一个为主题材（决定主属性），其余为副题材（微调 heat）。
     * heat = round(baseScore × 类型系数[genre] × (主题材系数 + Σ(副题材系数−1)×0.3))，
     * 见 ScriptSystem.computeHeat / GameConfig.GENRE_HEAT_FACTORS / THEME_FACTORS / THEME_SUB_FACTOR_WEIGHT。
     */
    themes: ScriptTheme[];
    /**
     * 剧本热度：角色名气加成 = heat × 名气系数[番位]（GameConfig.FAME_BILLING_FACTORS）。
     * 剧本级单一数值，按 heat 公式（导入台账 §1）由 baseScore + genre + themes 推导。
     */
    heat: number;
    /**
     * 基础评分（2-10，类比豆瓣评分）：剧本本身质量。
     * 实际评分 = 基础评分 + 番位系数 × (主角属性 − 角色要求) 的差值加成（clamp 2-10）；
     * 口碑影响 = (实际评分 − 6) × 口碑系数。
     */
    baseScore: number;
    /**
     * 播出期覆盖月数（批6）：缺省不填 = 按类型查 GameConfig.AIR_MONTHS_BY_GENRE
     * （短剧/电影/其他 1 个月，网剧/电视剧/综艺 2 个月）。
     * 仅特殊剧本（长剧集联播/特别企划）显式填写，纪律同角色 reward：
     * 必须行内注释写明缘由 + 在 docs/剧本/导入台账.md override 清单登记；常规剧本 ⛔ 不填。
     */
    airMonths?: number;
    /**
     * 拍摄期覆盖月数（2026-09-28 剧级统一档期批）：缺省不填 = ⌈最小番位角色 shootWeeks ÷ 4⌉ 个月（⛔ 进一法——档期是杀青底线，不得小于 lead 戏份）。
     * 仅叙事破格显式填写，纪律同 airMonths override：
     * 必须行内注释写明缘由 + 在 docs/剧本/导入台账.md 拍摄周期 override 清单登记；常规剧本 ⛔ 不填。
     */
    shootMonths?: number;
    /**
     * 剧宣专属宣传口径（剧宣批本批只留字段，⛔ 一部不填）：填了该剧的剧宣帖只发此预写文案、
     * 不抽通用模板池——留给年度大戏级重点剧的"官方官宣口径"。纪律同 airMonths/reward override：
     * 必须行内注释写明缘由 + 在 docs/剧本/导入台账.md override 清单登记；常规剧本 ⛔ 不填。
     */
    promoCopy?: string;
}

/**
 * 特殊剧本附表条目（2026-10-06 定稿，设计基准 docs/特殊剧本实装设计.md）：
 * 只放特殊专用字段；剧本内容本体（name/roles/themes/heat/baseScore…）一律在普通剧本表，
 * 以 scriptId 一对一关联。全部特殊剧本汇总在 tables/SpecialScripts.ts。
 * 三类判别：不在附表=普通剧本（进排期）；有 availableSeasons 无 unlockFlag=定时上架型；
 * 两者都有=剧情解锁型（flag 置位前对玩家不存在）。
 */
export interface SpecialScriptDef {
    /** 指向普通剧本表里的那条（内容在那里，此处不重复） */
    scriptId: string;
    /** 自持上架窗口（白名单，逐季在架）：不进排期表、不被市场换新；['Y1Q2','Y1Q3']。
     *  只管**玩家可见**（季末重建与点灯插架两处同读）——2026-10-13 起 NPC 收口派角不再读它
     *  （见 GameState.sweepClosingScripts）。 */
    availableSeasons: string[];
    /** 存在开关（可选）：置位前这部剧对玩家完全不存在（试镜列表/档案不可见）。
     *  置位者 = FixedEvents 剧情事件的 unlockScripts；不设置 = 定时上架型，到点上架全服可见。
     *  2026-10-13 茄子改判：NPC 侧同受此闸（旧口径「NPC 派角不受此字段影响」作废——实测它让
     *  NPC 在玩家最早能偶遇导演的 7 月之前、6 月收口夜就把《颠倒姻缘》整本接走）。 */
    unlockFlag?: string;
    /** 竞角必得位（可选，按 `roles[].name` 列）：这些位**试镜通过后竞角不再掷骰，直接记为「竞得」**。
     *  2026-10-14 茄子定的机制形状，语义＝「这部剧的这个位就是留给你的」——剧情上说得通才填（导演就是冲着玩家写的角）。
     *  只管竞角那一道掷点，四件事一律不动：试镜门槛照查（属性不够照样进不了队）、
     *  定角夜照等（结果仍那一夜才端出来）、NPC 侧照旧（玩家不签，位同夜仍交 NPC 开拍）、存档不升版（静态表数据）。
     *  生效点全库唯一＝`ScriptSystem.revealAuditions`（它把 true 写进 `auditionReveal`，
     *  所以 10-14 收进系统层的那道签约门不必改一字，判据仍只有一把尺）。
     *  名单为空/缺省＝本剧没有必得位。现网只有《颠倒姻缘》填了这一项（柳惊蛰／李令仪，2026-10-14 茄子点名），
     *  承天／无此镇／十二阶留空。读这个字段的地方只许有一处（`ScriptSystem.revealAuditions`），别处再判一遍就是第二把尺。 */
    autoWinRoles?: string[];
    /** ★ 剧情钩子：这部剧的生命周期节点触发的特殊剧情（绑定关系全在这） */
    plotHooks: PlotHook[];
}

export interface PlotHook {
    /** 事件源：玩家签下专属位进组 / 杀青 / 开播（发在开播当月月末）/ 评分揭晓（发在播出完结那个月月末）
     *  10-14 茄子令：原名 `airBoom`（热爆）改叫 `airStart`（开播）——它触发的剧情写的是「开播三天/上线一周」，
     *  名字却叫爆线达成，与时点对不上；`airBoom` 这个值一并撤掉（没有另一条按热度爆线触发的口）。 */
    when: 'cast' | 'done' | 'airStart' | 'rating';
    /** 触发 FixedEvents 表里的哪个事件 */
    event: string;
    /** 延迟：事件源发生后隔 N 个月才触发（缺省当月立即） */
    afterMonths?: number;
    /** 附加条件（全部满足才派发） */
    cond?: {
        heat?: number;
        rating?: number;
        rank?: number;
        fame?: number;
        favor?: Record<string, number>;
        flags?: string[];
    };
    /** 默认过时不候：到期时 cond 不满足 → 作废跳过（剧本钩子多为时效内容）；
     *  显式 true = 挂起等待。主线可达性一律由全局剧情事件（FixedEvents.trigger）负责，不依赖此字段。 */
    wait?: boolean;
}

/**
 * **全局通告钩子**（2026-10-24 通告事件批）：**所有通告共享**的通用事件登记——
 * 与 `plotHooks` 正交：那边是「这部剧本自己的专属叙事」（登记在剧本数据上、文案写死），
 * 这边是「某类通告在某时点都会触发的通用事件」（登记在全局表、文案模板＋插值）。
 * 判定＝`PlotSystem.onNoticeHook` 一处（type×when 命中 → 掷 chance → fire 并带剧本上下文）；
 * 信号源＝`core/EventBus.emitNoticeHook`（全库唯一发射口）。
 * 扩展三件事互不相扰：加事件=本表加行；加时点=发射侧加一行+`NoticeHookPayload.when` 扩枚举；加通告类型=新系统调发射函数+`type` 扩字面量。
 */
export interface NoticePlotHook {
    /** 通告类型（与 `NoticeHookPayload.type` 同枚举） */
    type: 'drama';
    /** 时点（与 `NoticeHookPayload.when` 同枚举）：done=杀青／shootWeek=拍摄周行程执行后（非杀青周）／
     *  premiere=开播当月的月初（玩家点「确认行程」那一刻，本月就是这部剧的开播月） */
    when: 'done' | 'shootWeek' | 'premiere';
    /** 触发 FixedEvents 表里的哪个事件（事件本体 trigger 留空、repeatable: true——限频靠「一部剧只杀青一次」这类自然事实） */
    event: string;
    /** 概率（0~1，缺省 1=必发）。挂表不挂事件：同一事件将来可被不同钩子以不同概率复用 */
    chance?: number;
    /**
     * **周期粒度**（2026-11-03 茄子令「同类型事件以月为单位的每月只触发一个、以周为单位的每周只触发一个」）：
     *  - 缺省 `'week'`：一周内同粒度只派发一条。**这条闸不用新建**——一周只有一个 work 槽（`player.schedule[weekIdx]`），
     *    拍摄周与杀青天然错开（杀青周只发 done 不发 shootWeek），所以一次信号＝一周的份额，禁止再加计数状态。
     *  - `'month'`：本月内已派发过任意一条月粒度钩子，后面的让位。判据读 `PlotMessage.pushedAbsMonth` 现成字段，
     *    禁止新增存档字段、禁止升 SAVE_VERSION。
     * 同一次信号里多条命中时**必发的排在前**，它占了位后面的概率条连骰都不掷（茄子同日令「固定触发的不用再掷随机」）。
     */
    per?: 'week' | 'month';
    /**
     * 触发条件（全满足才派发；判定口＝`PlotSystem.onNoticeHook` 一处）。
     * 三类判据正好对应三个数据源：
     *  - `rank`＝玩家态（咖位 ≥ 此值）；
     *  - `billingMax`＝**剧上下文态**（payload 带的角色的番位 ≤ 此值，1=一番 3=三番；
     *    综艺类无番位（billing 缺省）＝不满足，想放行就别写本条）；
     *  - `genres`＝**剧本态**（payload 的 refId 反查 `SCRIPT_MAP[].genre` ∈ 此表，2026-11-03 加：播出期事件只给电视剧/网剧）。
     */
    cond?: {
        rank?: number;
        billingMax?: number;
        genres?: ScriptGenre[];
    };
}

/**
 * 特殊 NPC 附表（2026-10-06 定稿，设计基准 docs/特殊剧本实装设计.md §2.4）：
 * 社交全套（聊天/邀约/送礼/熟遇文案）在 NPCDef 主表；本表只放特殊专用字段。
 * 三类组合：资源型（meet+favorPlot）/ 竞争型（再加 rival，开局实例化为 NormalNpc 注入派角池）。
 */
export interface SpecialNpcDef {
    npcId: string;                 // 'npc_zhuangwenyin'（指向 NPCS 表）
    /** 结识渠道（可多个，错过其一还有别的渠道——渠道错过了人还在，只是晚认识） */
    meet: {
        /** ★ 初遇事件候选（有序，按序取第一个未触发过的派发）：不同时期初遇剧情不同——
     *  各候选事件自带 trigger 时间窗/条件互斥（前一个触发过或玩家选"不见"即永久关闭），
     *  末位候选可缺省 trigger 当兜底。encounter 载体：可长对话/多选项/分支/steps。
     *  双入口：①事件自带 trigger 剧情定时触发（PlotSystem 月度扫描）；②拍摄周偶遇池抽中指名派发
         *  （EncounterSystem.roll，不经事件 trigger）。事件内以 meetNpcs 完成结识。 */
        events: string[];
        /** 偶遇池来源剧本：拍摄周抽中该 NPC 的前提（普通池/特殊池剧本皆可） */
        viaScript?: string;
    };
    /** 好感分段剧情：结识后按好感档解锁，event 指向 FixedEvents（三载体通用） */
    favorPlot: { at: number; event: string }[];
    /**
     * 固定咖位（资源型特殊 NPC 的生涯视图；竞争型走 rival.rank）。
     * 用于固定阵容参评时的 %咖位名% 等展示，不参与评选总分。
     */
    rank?: number;
    /**
     * 送礼喜好类别（自由出行批预留，草案待文案填写表定）：省略 = 未定（送礼不触发 ×2）。
     * 普通 NPC 的喜好在性格模板（NormalNpcData.PersonalityTemplate.favCategory），不走这里。
     */
    favCategory?: import('./tables/Gifts').GiftCategory;
    /**
     * 竞争型专用（演员类）：固定咖位/名气，开局实例化为 NormalNpc 注入派角填充池
     * （身份字段从 NPCDef 派生——同一 id 的「社交视图 + 生涯视图」，⛔ 非双份维护）。
     */
    rival?: {
        rank: number;              // 登场咖位（乔子珊 5 / 许澄 5 / 罗一鸣 5，茄子定）
        fame: number;              // fame 起点（runtime 由滴灌/过气衰减自然浮动，⛔ 不给死年份咖位）
        /**
         * 名气收入倍率（单人覆盖）：省略 ⇒ 走 `GameConfig.NPC_FAME_INCOME_MULT`（默认 ×30）。
         * 数值按人设定值——罗一鸣「爬得急天花板低」用默认 ×30；乔子珊「起点高但滞涨」给 ×60。
         * ⛔ 普通 NPC 不吃这个倍率（全池抬水位会挤占玩家的当季角色位，见 GameConfig 注释）。
         */
        fameIncomeMult?: number;
        /** 接戏限制（2026-10-20）：只约束随机派发，不影响 roleAnchors 的固定安排 */
        auditionLimits?: {
            /** 只接的类型，省略 = 不限 */
            preferredGenres?: ScriptGenre[];
            /** 不接的类型，省略 = 不限 */
            excludedGenres?: ScriptGenre[];
            /** 只接的题材（命中任意一个即可），省略 = 不限 */
            preferredThemes?: ScriptTheme[];
            /** 不接的题材，省略 = 不限 */
            excludedThemes?: ScriptTheme[];
            /** 最高番位（如 2 = 只接一番/二番），省略 = 不限 */
            maxBilling?: number;
            /** 不接戏的年份区间数组，省略 = 不限；如 [{from: 3, to: 5}] 表示第3~5年不接 */
            blockedYears?: Array<{ from: number; to: number }>;
            /**
             * **阶段类型排除**（2026-11-03 新增，黎青「第 8 年起不接短剧」）：
             * 按年段排除剧本体裁，取数与 `phaseRoleLevelCaps` 同口径——取**最后一个 `fromYear ≤ 当年`** 的档。
             * 只约束**随机派发**，不影响 `roleAnchors` 剧情锚点。
             */
            phaseExcludedGenres?: Array<{ fromYear: number; genres: ScriptGenre[] }>;
            /**
             * **阶段题材排除**（2026-11-03 新增，黎青「第 8 年起不接偶像/仙侠/恐怖」）：
             * 按年段排除剧本题材（`ScriptTheme`），取数口径与 `phaseExcludedGenres` 相同。
             * 命中任意一个排除题材即不接；只约束**随机派发**，不影响 `roleAnchors` 剧情锚点。
             */
            phaseExcludedThemes?: Array<{ fromYear: number; themes: ScriptTheme[] }>;
            /**
             * **阶段接戏门槛**（2026-10-24 用户改令；规格=docs/特殊人脉/README.md §三；数值定值=导入台账 §7.3 ④‴）：
             * 按**年段**限制她能接的**角色等级**上限 —— 竞争型「软限制」的唯一新增旋钮。
             * 为什么改（口径沿革）：2026-10-23 的甲案曾给她排**逐年锚点曲线**（哪一年必须哪一档），
             * 用户 10-24 改令「不需要硬限制、不要求必须在哪一年是哪档；文档写的是预期方向，
             * 可以用限制不同阶段的接戏门槛来软限制名气和咖位」⇒ 曲线整套作废，咖位重新**浮动**：
             * 由「她能接到什么档的戏 → 滴灌名气涨多少」自然推出（与普通 NPC 同一条时钟）。
             * 门槛只约束**随机派发**，不影响 `roleAnchors` 剧情锚点（两个女主位照旧 fallback）。
             * `fromYear` ＝展示年（第 1 年 = 1，与 `blockedYears` 同口径），取**最后一个 `fromYear ≤ 当年`** 的档；
             * 省略/空数组 ⇒ 不限。数值按“取最新满足档”的写法落表（数组按 fromYear 升序书写）。
             * ⚠️ 已知局限（README §三 明文登记）：派角①是「等位优先→向下顺延→向上兜底」，
             * **角色等级门槛不等于咖位硬上限**——她咖位越线后仍会被低位档兜底接戏。
             * 真天花板是否需要独立判据，等 QA 实测（18 年后罗一鸣 rank ≤ 7、乔子珊 ≤ 6）说话，
             * 不到才回来加；⛔ 现在不预造第二把尺。
             */
            phaseRoleLevelCaps?: Array<{ fromYear: number; maxRoleLevel: number }>;
        };
    };
    /**
             * 角色锚点（2026-10-15）：本特殊 NPC 与某剧本某角色的绑定。
             *  - `exclusive`＝必定饰演（对玩家不可见、不可试镜；派角时该位直接给她）
             *  - `fallback`＝玩家优先（玩家可试镜；玩家**未占到**该位时才兜底给她——竞角输或未签才算「未占到」）
             * `role` 用角色名（比 roleIndex 稳，角色增删不漂移）。全库唯一读取口＝`roleAnchorOf`，别处再判一遍即第二把尺。
     */
    roleAnchors?: RoleAnchor[];
}

/** 角色锚点（见 SpecialNpcDef.roleAnchors）：特殊 NPC 与某角色位的绑定 */
export interface RoleAnchor {
    scriptId: string;
    /** 角色名（对应 ScriptRoleDef.name） */
    role: string;
    /** exclusive=必定饰演（玩家不可见/不可试镜）；fallback=玩家优先、玩家未占到才兜底给她 */
    mode: 'exclusive' | 'fallback';
}

/**
     * 固定剧情事件（2026-10-06 定稿，设计基准 docs/特殊剧本实装设计.md §2.3）：
     * 全局事件（trigger 触发：calendar/数值/flag）与剧本钩子事件（SpecialScripts.plotHooks 命中触发）
     * 共用同一结构、同一演出引擎（PlotSystem）。三载体：encounter 偶遇 VN / message 人脉消息 / media 媒体弹窗。
 */
/** 演出宿主（`FixedEventDef.playOn`）：决策页 / 执行档（月度执行页＋自由出行地图同属执行档）/
 *  阶段演出（全屏场景＋VN，审查线用）。
 *  缺省口径在 `PlotSystem.pendingFor` 一处实现，别在宿主里各写一遍。 */
/**
 * 演出宿主（**在哪演**）：
 *  - `decision`＝决策页，排在点「确认行程」之后、颁奖夜之后、行程执行之前演（爆火那类剧情）；
 *  - `decisionEntry`＝**决策页入场**那一档（影视热度榜之前演）——数值/舆论类事件住这儿
 *    （2026-10-22 用户令「每个月执行完行程之后在决策页触发，在每月的影视热度表之前」）；
 *  - `execution`（缺省，数据里不必写）＝月度执行页的演出档，以及自由出行地图的到访演出；
 *  - `stageStory`＝阶段审查演出的全屏宿主（信息栏＋场景底图＋VN 卡，与月度执行页同构），在决策日入场时播。
 */
export type PlotHost = 'decision' | 'decisionEntry' | 'execution' | 'stageStory';

/**
 * **数值门槛**（所有触发口径共用的一套判据；⛔ 旧的 trigger.rank/fame/charm/stage/favor/tags 已迁进这里）。
 * `attr`／`attrMax` 走八维属性（**口碑**是八维之一）；其余是属性之外的读数。
 */
export interface EventRequire {
    /** 属性**下限**（例：`{ 演技: 400 }` ＝ 演技 ≥ 400） */
    attr?: Partial<Record<AttributeKey, number>>;
    /** 属性**上限**（例：`{ 口碑: -100 }` ＝ 口碑 ≤ -100） */
    attrMax?: Partial<Record<AttributeKey, number>>;
    /** 咖位下限 */
    rank?: number;
    /** 咖位上限（≤） */
    rankMax?: number;
    /** 生涯阶段下限（`GameState.currentStage`） */
    stage?: number;
    /** 名气下限 */
    fame?: number;
    /** 粉丝下限 */
    fans?: number;
    /** 资金下限 */
    money?: number;
    /** NPC 好感下限（键＝NPC id） */
    favor?: Record<string, number>;
    /** 必须已有的标签 */
    tags?: string[];
}

/**
 * **事件触发口径**（条目级：这条事件什么时候进队）。
 * 三类口径共用这一个结构：
 *  ① 固定剧情／时间线剧情（`calendar`／`location`／`scriptAirStart`／`requireFlags`…）；
 *  ② 数值事件（`playOn: 'decisionEntry'` ＋ `require` ＋ `weight`：先过咖位总闸，再在池内按权重抽）；
 *  ③ 剧本钩子派发（`SpecialScripts.plotHooks`，条件走钩子自己的 `cond`）。
 */
/**
 * **按口碑分档的一档**（`maxRep` ＝ 口碑 ≤ 本值适用这一档）。
 * 口碑是运行时值（-500~500）；档位边界取自评价面五档（`GameConfig.REP_BAND_EDGES`）。
 */
export interface RepBand {
    /** 口碑上限（≤ 本值适用这一档） */
    maxRep: number;
}

/** **池内权重随口碑分档**（`EventTrigger.weight` 的档位表形态，2026-10-22）。 */
export interface WeightBand extends RepBand {
    /** 这一档的抽取权重（相对值，≥ 0；**0 ＝ 这一档抽不到**，更直白的挡法是用 `require`） */
    weight: number;
}

/** **评论区随口碑分档**（`FixedMedia.commentsByRep` 的档位形态）。 */
export interface CommentBand extends RepBand {
    /** 这一档的楼层评论 */
    comments: string[];
}

export interface EventTrigger {
    /** 数值门槛（全满足才谈触发） */
    require?: EventRequire;
    /**
     * **池内抽取权重**（2026-10-22 用户令「先按咖位掷骰决定是否触发，再从符合门槛的事件里抽」）。
     *
     * 数值事件（`playOn: 'decisionEntry'`）的判定分两道闸，本字段只管**第二道**：
     *   ① 总闸＝`GameConfig.NUMERIC_EVENT_RANK_CHANCE[咖位-1]`（Lv.1~3 给 0 ＝ 不演；每月至多一条）；
     *   ② 池内抽取＝门槛全满足的事件里按本权重轮盘（缺省 1）。
     *
     * **也可以给一张按口碑分档的表**（`WeightBand[]`）：取当前口碑命中的那一档的 `weight`
     * ⇒ "口碑越差，这条黑帖被抽中的几率越高"。
     * 旧的 `trigger.probability`（每条事件各自掷概率）已退役，数值事件写它不生效。
     */
    weight?: number | WeightBand[];
    /** 日历窗口（月粒度，1-indexed 展示口径） */
    calendar?: {
        from: { year: number; month?: number };
        to: { year: number; month?: number };
    };
    /** 前置剧情 flag */
    requireFlags?: string[];
    /** 反向条件：这些 flag 一个都不能有 */
    absentFlags?: string[];
    /** 咖位上限（严格小于，⛔ 与 `require.rankMax`（≤）不是一回事：这是"审查线前才触发"那类反向门） */
    rankBelow?: number;
    /** 地点条件（到访才触发） */
    location?: string;
    /** 某剧开播时触发（与玩家是否参演无关） */
    scriptAirStart?: string;
}

/**
 * **剧情/事件条目**（一套结构，三类来源共用：固定剧情／时间线剧情／数值事件）。
 * ⛔ 旧名 `FixedEventDef` 保留为别名（勿新增用法）。
 * ⛔ 另有一套 `EventDef`＝**月度随机事件**的条目（带 gates/骰子选项），本批**不并入**（它有"判定成功/失败"的独有机制）。
 */
export interface PlotEventDef {
    id: string;                        // 'FIXED_901_DONE_NIGHT'
    /** 单体事件载体；有 steps 时按序列演出、本字段忽略 */
    medium: 'encounter' | 'message' | 'media';
    /**
     * 演出宿主（见 `PlotHost`）：
     *  - `decision`＝决策页，排在点「确认行程」之后、颁奖夜之后、行程执行之前演；
     *  - `execution`（缺省，数据里不必写）＝月度执行页的演出档，以及自由出行地图的到访演出；
     *  - `stageStory`（2026-10-21 加）＝阶段审查演出的全屏宿主（信息栏＋场景底图＋VN 卡，与月度执行页同构），
     *    在决策日入场时播（`app.show('decision')` 先扫本片队列），失败局的终局要等它演完。
     * 茄子的口径：以后播出侧那类特殊剧情都不在月度执行过程中触发；执行档暂定只留两类——
     * 按地点到访触发的、拍摄通告期间触发的。队列条目现场按本字段分派宿主（不落存档：
     * 老档与 `PlotMessage` 都不增字段；表内查无的事件按执行档处理，由那边的「查无」warn 点名）。
     * 粒度＝整条事件一个宿主：`steps` 不逐拍分场地（一场戏不会一半在决策页、一半在片场）。
     */
    playOn?: PlotHost;
    /**
     * **可重复**（2026-10-22 用户令）：默认一次性——`fire`/`deliver` 用 `player.flags[event.id]` 记住，
     * 触发过就再不触发。写 `true` ＝ 这条不置灯、也不被灯挡：可以反复上屏（黑帖那类"同一件事反复被翻出来"）。
     * 限流靠三件事兜住，⛔ 不需要新的存档字段：① 每月只扫一次（同月不会重掷）
     * ② 数值事件每月只抽一条（`PlotSystem.rollNumericEvent`）；③ 同一事件在队里没演完时不会重复入队。
     */
    repeatable?: boolean;
    /** 本事件的场景底图（`data/SceneBgs.ts` 的键）；只在 `playOn: 'stageStory'` 上有意义，
     *  缺省＝纸白底。按事件而不是按宿主给：一条剧情的场地由文案决定（出租屋/片场/酒店各不同）。 */
    scene?: SceneKey;
    /** 事件触发口径（见 `EventTrigger`；剧本钩子事件由 plotHooks 命中，不走 trigger） */
    trigger?: EventTrigger;
    /** message 载体：发信人脉 NPC */
    npcId?: string;
    /** encounter/message 正文 */
    description?: string;
    /** 该事件带选项（软分支：effects/setFlags） */
    choices?: FixedChoice[];
    /** media 载体内容（新闻/讨论贴 DOM 弹窗+评论区） */
    media?: FixedMedia;
    /** ★ 多步剧情序列：按顺序逐步演出（玩家确认一步 → 演下一步）；分支=步级 requireFlags+选项 setFlags */
    steps?: FixedStep[];
    /** 执行效果 */
    setFlags?: string[];
    /** 剧情解锁型剧本的存在开关（走 `GameState.unlockScript`：置 player.flags[unlockFlag]
     *  + 当场把剧挂进当前货架，2026-10-14 茄子令「不用一季一季等换新」） */
    unlockScripts?: string[];
    /** NPC 好感增减 */
    favor?: Record<string, number>;
    /** 触发即生效的玩家数值（键=EventSystem.applyEffects 现成键） */
    effects?: Record<string, number>;
    /** 剧热度加值：写入 player.plotHeatBonus（2026-10-10 茄子定：剧情热度不走剧宣账本、无封顶；
     *  `FanSystem.workHeatOf` 与剧宣/客串的 heatPromoted 两本账一起读）。
     *  `scriptId` 缺省＝**本次派发带入的那部剧**（通告钩子事件的 payload 上下文），静态挂剧的事件请照旧写明 id。 */
    scriptHeat?: { scriptId?: string; delta: number }[];
    /** ★ 结识 NPC：事件执行时把这些特殊 NPC 置 0 入人脉页（初遇事件替代 encounter 系统的 meet:true） */
    meetNpcs?: string[];
}

/** ⛔ 旧名别名（勿新增用法）：事件条目现在叫 `EventDef`。 */
export type FixedEventDef = PlotEventDef;

/** 序列步：每步自己的载体/内容/数值；requireFlags 不满足的步跳过（岔路表达） */
export interface FixedStep {
    medium: 'encounter' | 'message' | 'media';
    npcId?: string;
    description?: string;
    choices?: FixedChoice[];
    requireFlags?: string[];
    /** 角色位过滤（可选）：玩家饰演该角色时本步才演出，否则跳过。
     *  支持 `!` 前缀反向（`onlyRole: '!柳惊蛰'` = 玩家不是该角色时演出）。
     *  例：热爆事件的许林辉私信只发给饰演女主的玩家（2026-10-10 茄子定）。 */
    onlyRole?: string;
    /** 仅当玩家在该剧有合同（演过）时才演这一步：值为 scriptId；未参演则整步跳过。
     *  例：热爆四拍的步骤 1 热议帖、小助理点评、许林辉私信只在玩家演了《颠倒姻缘》时演；
     *  步骤 2 新闻卡不带本字段＝全网都能看（2026-10-18 加）。 */
    onlyIfCast?: string;
    media?: FixedMedia;
    effects?: Record<string, number>;
    /** 该步的 NPC 好感增减 */
    favor?: Record<string, number>;
    /** 该步的剧热度加值（`scriptId` 缺省口径同事件级） */
    scriptHeat?: { scriptId?: string; delta: number }[];
    setFlags?: string[];
    /** 按所演角色叠加的数值（在步级 effects 之外）：命中所演角色时生效。
     *  例：热爆事件小助理步——女主玩家名气+50、女二玩家名气+30（2026-10-10 茄子定）。 */
    roleEffects?: { onlyRole: string; effects: Record<string, number> }[];
}

/**
 * 剧情一拍要改的每一笔账（2026-10-15 茄子令「剧情改的数要当场看得见」）。
 * 由 `PlotSystem.beatEffects` 从事件表＋存档现算，执行侧逐条落账、显示侧逐条组变动行——
 * 同一份清单、同一套 `onlyRole` 判据。message 拍的那一份会随气泡存根落档（`PlotChat.effects`），
 * 落账时机＝投递那一刻（无回复）或玩家选定回复那一刻（有回复，不选就不加）。
 */
export interface PlotBeatEffect {
    /** 变动主体：attr=属性/资源（键表同 `eventApplyEffects`）· scriptHeat=某部剧的热度 · favor=NPC 好感 · meet=结识 NPC */
    kind: 'attr' | 'scriptHeat' | 'favor' | 'meet';
    /** attr: 效果键（fame/money/charm…）；scriptHeat: scriptId；favor/meet: npcId */
    id: string;
    /** attr/scriptHeat/favor: 增减量；meet: 恒 0（结识没有量，变动行显示「已结识」） */
    value: number;
}

/** media 版式：card=标准图文卡（news 缺省）；paper=报刊头双线排版（news 版式之一）。hotpost 单版式不填 */
export type MediaStyle = 'card' | 'paper';

/** media 载体内容：新闻/热议讨论贴/微博（DOM 弹窗+评论区，可插值 {script} {heat} {rating}，配图可选位） */
export interface FixedMedia {
    /** news=正规媒体快讯（星闻速递）；hotpost=观众自发讨论帖（论坛·楼主+楼层）；
     *  weibo=营销号微博（参照新浪微博详情页：博主行+正文+互动栏+热评，2026-10-17 新增） */
    kind: 'news' | 'hotpost' | 'weibo';
    /** news 版式：缺省 'card' 图文卡；'paper' 报刊排版。hotpost/weibo 单版式无需填 */
    style?: MediaStyle;
    /** 主标题（news 的新闻标题 / hotpost 的帖子标题）；**微博无标题**——kind:'weibo' 时留空、不渲染，
     *  仅 doDialogLabel 兜底可及名。 */
    title?: string;
    body: string;
    /** 配图位（news·card / weibo 用）：写相对站根的 `media/<文件名>`（图片本体放 web/public/media/），
     *  未提供＝纯文字；文件缺失/写错名＝藏图（不显示破图）。规则见 docs/特殊剧情/填写格式.md §配图 `image` */
    image?: string;
    /** weibo 博主名（营销号名，如「娱圈八爷」）；缺省回退固定号。news/hotpost 不填。
     *  后援会这类「挂着玩家脸的号」写成 `{player}后援会`（插值见 `plotStage.mediaBeatOf`）。 */
    author?: string;
    /** weibo 博主头像：`'player'` ＝ 用玩家当前捏脸头像（后援会官方号，2026-10-23 用户令）；
     *  缺省 ＝ 按博主名 hash 出「首字色块」占位。news/hotpost 不填 */
    authorAvatar?: 'player';
    /** 评论/回应正文（一条一立场，数据只存内容）；昵称+头像由系统生成——⚠️ 网名池当前⛔ 上屏，
     *  一律显示 `momo`（2026-10-13 用户令 B：hotpost 与评价面板同步先穿 momo，不提前启用池，等最后统一改）
     *  （hotpost 必填、news 常空） */
    comments: string[];
    /**
     * **评论区随口碑分档**（2026-10-22 用户令「评论做差分」）：存在时**覆盖** `comments`。
     * 取当前口碑命中的那一档（`bandOf`，边界与 `GameConfig.REP_BAND_EDGES` 同源）；
     * 一档都没有命中的 ⇒ 落回 `comments`。
     * 典型用法＝低口碑档深信不疑并添油加醋、中口碑档将信将疑、高口碑档压根不信（骂营销号造谣）。
     */
    commentsByRep?: CommentBand[];
    /** 话题度＝这条 media 舆论读数（news 讨论/转发、hotpost 楼层点赞）的量级锚。
     *  尺与剧热度同源，四档参考：5 没人搭理 / 30 有点动静 / 120 吵翻了 / 400 全网刷屏。
     *  **取数链**：表里填了用它 ⇒ 否则**数值事件**（`playOn:'decisionEntry'`）按当下**名气**换算
     *  （`GameConfig.NUMERIC_EVENT_BUZZ_FROM_FAME`，2026-10-22 用户令「名气越高话题度越高」）⇒
     *  否则回退该事件所挂剧的当前热度（`workHeatOf` 活尺）。
     *  既没填、也不是数值事件、又反查不到剧（八卦、盘点帖这类不带剧的条目）就落兜底档并 console.warn，
     *  所以**这类 media 必须自己填这一格**。换算比例见 `MediaPanel` 的 BUZZ 表。 */
    buzz?: number;
}

/** 剧情选项（direct 直效；软分支用 setFlags 点灯，分支步 requireFlags 接） */
export interface FixedChoice {
    text: string;
    /** NPC 在玩家选此选项后的后续回复（message 载体专用：选完选项追加一条 NPC 气泡） */
    reply?: string;
    effects?: Record<string, number>;
    setFlags?: string[];
}

/** 剧情收件箱条目（PlotSystem 推送；UI 按 event.medium 渲染：message 对话 / media 讨论贴 / encounter 偶遇） */
export interface PlotMessage {
    /** FixedEventDef.id */
    eventId: string;
    /** message 载体的发信 NPC */
    npcId?: string;
    /**
     * **剧本上下文**（2026-10-24 通告事件批）：通告钩子 fire 时带入（哪部剧/哪个角色位），
     * 插值 `{script}`/`{role}` 与「同事件不同剧」的查重都读它。非钩子事件不带（undefined），
     * 旧档缺字段自然兼容。
     */
    scriptId?: string;
    /** 同上：玩家饰演的角色位（{role} 插值用） */
    roleIndex?: number;
    /** 已交付＝演出已交到玩家眼前（2026-10-13 形状 C 收口：这一枚同时是结算闸门——
     *  flag、结识、首步效果都在 `PlotSystem.deliver()` 里跟着它一起做；
     *  false＝已入队但还没演过，效果不许未到账。语义由旧「已读」原地收窄，不许升存档版本） */
    read: boolean;
    /** 已处理（选项已选/步骤演完） */
    resolved: boolean;
    /** 多步剧情的当前步指针（无 steps 的事件恒 0） */
    stepIndex: number;
    /** 推送的绝对月份（year*12+month，排序用） */
    pushedAbsMonth: number;
}

/** 剧情聊天消息存根（2026-10-14 茄子定：message 分类的剧情=人脉页聊天页发的消息，月底统一发送）。
 *  一条存根=聊天流里的一颗 NPC 气泡（多行文案拆行投递）；聊天页回放、行级红点提示未读。 */
export interface PlotChat {
    /** 发信 NPC（解析不到具体 NPC 的叙述拍兜底 'npc_assistant'，经纪人页展示后续再接） */
    npcId: string;
    /** 气泡文本（已剥「NPC名：」前缀与插值） */
    text: string;
    /** 发送绝对月份（year*12+month，排序用） */
    absMonth: number;
    /** 玩家开过该 NPC 的聊天页即已读（红点消） */
    read: boolean;
    /** 女主回复选项（message 拍的 choices）。未答时入口＝聊天页底部待办清单那一行（2026-10-15 茄子定乙档，
     *  气泡流里不再画按钮——那颗米色胶囊被实测读成"已回复"）；玩家选定后气泡流里追加一条主角气泡 */
    choices?: string[];
    /** 玩家已选的回复下标（选过即定格，重进聊天页不再出这一行） */
    pickedChoice?: number;
    /** 各选项的 NPC 后续回复（与 choices **同下标**，没配的留空位；玩家选定后追加一条 NPC 气泡） */
    replies?: (string | undefined)[];
    /** 各选项的 setFlags（与 choices 同下标，可有空位；玩家选定后点灯，后续步 requireFlags 接住做分支） */
    choiceFlags?: (string[] | undefined)[];
    /** 各选项的 effects（与 choices 同下标，可有空位；玩家选定后即时结算） */
    choiceEffects?: (Record<string, number> | undefined)[];
    /** 这一拍本身要改的账（`PlotSystem.beatEffects` 投递那一刻算好落档，渲染时不许反查表——
     *  表以后改了，老气泡上的变动行不许跟着变）。带回复的拍要等玩家选定回复才落账（不选＝不加），
     *  不带回复的拍投递即落账；两种都只在落账之后才显形（2026-10-15 茄子定）。 */
    effects?: PlotBeatEffect[];
}

/** 事件选项基础字段 */
interface ChoiceBase {
    id: string;
    text: string;
    /** 选项效果：{ 属性或资源: 增减 }，如 { charm: 3, money: -100 } */
    effects: Record<string, number>;
}

/** 事件选项：直接型（默认开启，不判定、不掷骰，必定生效） */
export interface DirectChoice extends ChoiceBase {
    type: 'direct';
    /** 选后展示的结果文案（必生效） */
    resultText: string;
}

/** 事件选项：标签型（拥有指定标签才开启，不判定、不掷骰，必定生效） */
export interface TagChoice extends ChoiceBase {
    type: 'tag';
    /** 选后展示的结果文案（必生效） */
    resultText: string;
    /** 拥有该标签才显示此选项（未拥有则隐藏） */
    requiredTag: string;
}

/** 事件选项：判定型（根据一种属性 D6 判定，成功/失败导向不同结果） */
export interface CheckChoice extends ChoiceBase {
    type: 'check';
    /** 判定绑定属性 */
    bindAttr: AttributeKey;
    /** 判定难度 DC（公式：属性值 + D6×10 ≥ DC → 成功） */
    dc: number;
    /** 判定成功后的文案 */
    successText: string;
    /** 判定失败后的文案 */
    failText: string;
    /**
     * 判定失败时应用的负面效果（可选，`{ 属性或资源: 增减 }`）。
     * 用于「失败有代价」的场景（单项属性 -1 / 小额金钱 / 口碑 -1~-2）；
     * 纯错过机会的失败可不填（维持失败无效果）。
     * 键合法：可训练属性（charm/talent/temperament/stamina/eq/wisdom/artistry）/ reputation / money / fame / energy。
     */
    failEffects?: Record<string, number>;
}

/** 事件选项（三类互斥：直接 / 标签 / 判定） */
export type ChoiceDef = DirectChoice | TagChoice | CheckChoice;

/**
 * 通用条件闸门（第 45 批）：全站「按状态筛选」的统一语言，求值器在 `systems/GateSystem.ts`。
 * 多条之间 AND（全过才成立）；空数组/不写 = 无条件通过。
 * 新条件类型 = 扩 type 联合 + gatesPass 的 switch 加一支，⛔ 不在调用方另写 if。
 * 注：既有旧条件机制（成就 conditionType、训练 requirePathCounts、剧本市场 rank 窗口、邀约 favor≥40）
 *     本批起**不迁移**，逐处收口另行立项。
 */
export interface Gate {
    /** rank=咖位Lv / attr=单项属性（含口碑） / money=金钱 / energy=体力 / age=年龄 / flag=剧情标记 */
    type: 'rank' | 'attr' | 'money' | 'energy' | 'age' | 'flag';
    /** type='attr' 填属性 key（口碑=reputation）；type='flag' 填标记名 */
    key?: string;
    /** ≥（flag 型：≥1 = 要求已置位） */
    min?: number;
    /** ≤（flag 型：≤0 = 要求未置位） */
    max?: number;
}

/** 事件定义（随机事件池） */
export interface EventDef {
    id: string;               // EVENT_001
    title: string;
    description: string;
    /** 触发行程白名单：仅当周执行这些行程（ScheduleSlot.refId）时才可能触发；不写/空数组 = 任意行程。
     *  支持 `*` 前缀通配（第 45 批）：'SCRIPT_*' = 任意签约拍摄周（refId 为剧本 ID，不逐剧枚举） */
    triggerSchedule?: string[];
    /** 触发概率（每周行程匹配 triggerSchedule 时独立判定） */
    triggerProbability: number;
    /** 状态闸门（第 45 批）：先过闸门再掷概率。如咖位穿帮档 [{type:'rank',min:2}]、低口碑档 [{type:'attr',key:'reputation',max:20}] */
    gates?: Gate[];
    /** 选项（2-4个），每种选项自带成败文案/结果文案 */
    choices: ChoiceDef[];
}

/** 标签定义 */
export interface TagDef {
    id: string;               // TAG_001
    name: string;
    type: 'positive' | 'negative' | 'neutral'; // 正面/负面/中性
    /** 效果描述 */
    description: string;
    /**
     * 属性加成 { attr: 值 }，可省略——加成可能以其他形式实现（机制型/事件型等）
     */
    attributeBonus?: Partial<AttributeSet>;
    /** 获取条件（如咖位达到 Lv.5，或事件） */
    acquireCondition?: string;
    /** 删除条件（留空=永久） */
    removeCondition?: string;
    /** 显示优先级 */
    priority: number;
    /**
     * 归属池（2026-10-19 开局身份拆分 → 2026-10-21 特性点数制）：
     * - `origin`：开局身份，不进特性池，存 `PlayerState.origin`
     * - `trait`：开局特性，进建角页的**点数制**选择池（玩家花点数/负面回点数自由点选）
     * - 缺省（undefined）：局内事件/成就等获得的普通标签
     */
    pool?: 'origin' | 'trait' | 'event';
    /** 身份是否已解锁（仅 `pool:'origin'` 有意义；缺省按已解锁处理） */
    unlocked?: boolean;
    /** 上屏的一行优势/加成文案：身份卡「优势 · …」与特性卡「✦ …」共用（每个标签都写） */
    perk?: string;
    /**
     * 特性点数价（仅 `pool:'trait'` 有意义；**正数＝消耗点数，负数＝返还点数**）。
     * 负面特性＝负数：只带负效果（`attributeBonus` 为负）、不返还任何正加成，
     * 玩家用它换取更多点数预算（建角页点数制，见 GameConfig.TRAIT_*）。
     */
    traitPoints?: number;
    /**
     * 「训练减半」类负面特性：**被减半的属性键**。
     * 生效点**只有一处**——之后**训练获得**该属性的增量砍半（挂在 `AttributeSystem.applyChanges`，
     * 那是训练加属性的唯一入口 ⇒ 事件给的、杀青给的、商店买的、开局初始属性都不受影响）。
     * ⛔ **不砍开局底值**（2026-10-21 用户令）：挂上这条特性时初始属性原样不动。
     * 只砍**正向**获得（训练扣属性不砍）；一律**向下取整**。
     */
    halvedAttr?: AttributeKey;
    /**
     * 「奖越拿越多，奖项加成就越高」类**正面**特性：**每多一座已得的奖，之后每座奖的名气 +本值**。
     * 倍率 ＝ `min(awardFameCap, 1 + 已得奖数 × 本值)`；**已得奖数含本次领奖**
     * （领奖记录先入 `awardHistory` 再发钱 ⇒ 落点 `GameState.awardFameGain`）。
     * ⛔ 只乘**获奖**那笔名气（`AWARD_FAME_WIN`）：提名频次高，⛔ 不乘（否则变成刷名气的口子）。
     * ⛔ 口碑不乘（定额制）；⛔ 作品滴灌/营销营业/事件行程都不乘。
     * 落点＝`AwardSystem.payOut` 与 `payoutOfCeremony`（两处必须同口径：UI 读的就是后者）。
     */
    awardFameStep?: number;
    /** 「奖越拿越多」类特性的倍率**封顶**（缺省＝不封顶）。 */
    awardFameCap?: number;
    /**
     * 「营销减益」类**负面**特性：**营销渠道产出的名气 ×本值**。
     * ⚠️ **「营销渠道」是可扩展的口径**（2026-10-21 用户令「后面会加别的营销渠道，不只是发微博」）：
     *    凡属"靠经营/曝光换来的名气"都算营销渠道，**一律经 `GameState.marketingFameGain` 过一道**。
     *    现存渠道＝微博营业帖（整笔）／剧宣帖的「自营业」那半／公益帖（整笔）；
     *    将来加新渠道（如直播、代言、综艺曝光）时，**在新渠道的 fame 发放处调用
     *    `state.marketingFameGain(x)` 即可**，⛔ 别绕过它自己乘。
     * ⛔ **不算营销**：作品本身给的名气（杀青滴灌、剧宣帖的作品分红）、奖项、事件/行程。
     */
    marketingFameMult?: number;
    /**
     * 「黑料缠身」类**负面**特性：**开局一次性扣口碑**（负值，落点 `GameState.addTag`，走 `setAttribute`
     * ⇒ ⛔ 不被下面的口碑损失倍率再乘一次：这是基值不是"损失事件"）。
     * 2026-10-23 用户拍板 = −200（口碑域 −500~500，−200 已落在黑红路线 T1 深档）。
     */
    startReputationPenalty?: number;
    /**
     * 「黑料缠身」类**负面**特性：**每一次负向口碑变化 ×本值**（>1 才是负面；缺省 1）。
     * 落点＝`GameState.addAttribute`（全库口碑增减的总闸）⇒ 作品低分、事件失败分支、
     * 数值事件（黑帖）扣的口碑统统乘一次；**正向口碑不乘**（做好事不受连累）。
     * 取整＝**按绝对值四舍五入后带回符号**（−3 × 1.5 = −4.5 ⇒ −5，⛔ JS `Math.round(-4.5)` 的 −4）。
     */
    reputationLossMult?: number;
}

/** NPC 定义 */
/** ① 身份层：所有人形 NPC 公用（随机 NPC / 资源型特殊 NPC / 竞争型特殊 NPC） */
export interface NpcIdentity {
    id: string;
    name: string;
    /** 身份描述（特殊 NPC 的人脉页角色行：'三金影后'/'新锐导演'/'选秀小花'） */
    role?: string;
    gender?: 'male' | 'female';
    /** emoji 兜底头像（无捏脸配置时显示） */
    icon?: string;
}

/** ② 生涯层：演员类 NPC 公用（随机 NPC 全部 + 竞争型特殊 NPC 实例化后的运行时状态） */
export interface NpcCareerState {
    /** 名气（唯一成长值，播出期滴灌增加；整季无工可接则过气衰减，批6） */
    fame: number;
    /** 咖位等级（由 getRankFromFame(fame) 同步；批6 起可因过气衰减降级） */
    rank: number;
    /** 在途工作（拍摄期/播出期均在此，批6 取代旧 currentRoles「本季度接角记录」语义；市场换新不再清空） */
    activeJobs?: NpcJob[];
    /** 已完成作品履历（批6，播出完结时写入；NPC 评分 = 剧本 baseScore） */
    filmography?: FilmographyEntry[];
    /** 最后一次有工作的绝对月份（接角与完结时刷新）：换新判定 abs − 本值 ≥ 刷新周期 → 过气衰减 */
    lastWorkAbsMonth?: number;
}

/** ③ 社交文案层：静态定义 NPC 的专属社交文案（特殊人脉每人手写全套，⛔ 不复用随机 NPC 性格模板池，亦无共享兜底池） */
export interface NpcSocialCopy {
    /** 聊天文案池（对话）：`主角开场|NPC回复` 两段式，主角发起 → 主角气泡 + NPC 气泡各一条。每月 3 次「聊天」随机取一段，好感固定 +2，无选项 */
    chatTexts: string[];
    /** 邀约成功文案（好感 ≥40 邀约成功 +5 时 NPC 的回复） */
    inviteOkText: string;
    /** 邀约失败文案（邀约失败 +1 时 NPC 的回复） */
    inviteFailText: string;
    /** 赴约见面文案（周末咖啡馆赴约时 NPC 的见面句；单条——特殊 NPC 不设池） */
    inviteMeetText: string;
    /** 送礼文案池：送礼后 NPC 随机回复一条（%s = 喜好物名，文案可带） */
    giftTexts: string[];
    /** 探班（影视城探班抽中此 NPC 时）：开口行——每人手写专属（随机 NPC 走性格模板 visit 池） */
    visitMeet: string[];
    /** ★ 初遇开口（未结识时偶遇/公园抽中的开场句，同随机 NPC 的 firstMeet；有 meet.events 剧情事件时优先走剧情，此字段兜底） */
    firstMeet: string[];
    /** ★ 初遇反应（点「认识一下」后 NPC 的反应句，同随机 NPC 的 firstWarm） */
    firstWarm: string[];
    /** ★ 偶遇·熟遇开口（已结识；初遇不走这里——特殊 NPC 初遇是剧情事件 meet.event，可长对话/多选项/分支） */
    encMetMeet: string[];
    /**
     * 偶遇·熟遇选「闲聊几句」后的反应（好感 +4）。
     * 熟遇改版 2026-10-10：旧「认真聊 +5 / 点头寒暄 +3」两池并成一档（玩家永远点高的那档，二分是假选择）；
     * 偶遇闲聊⛔ 用人脉页 `chatTexts`（那是「主角开场|NPC回复」两段式，接在 NPC 已开过口之后语序不成立），
     * ⛔ 共用每月 3 次聊天额度。
     */
    encMetChat: string[];
    /**
     * ⚠️ 熟遇的「送礼 / 邀约」两格⛔ 另立偶遇池，直接复用上表的 `giftTexts` / `inviteOkText` / `inviteFailText`
     *  （用户 2026-10-10 定：邀约同文案池、⛔ 同额度——偶遇那一发不走 `SocialSystem.invite`、不扣每月 1 次邀约额度）。
     */
    /**
     * NPC 主动打招呼文案池（月初掷概率命中后，NPC 在聊天页发来的第一条消息）。
     * 特殊 NPC 手写专属文案；缺省时 fallback 到 GREETING_LINES['special'] 通用池。
     * 2026-10-14 用户令：NPC| 前缀条目从 chatTexts 迁来——NPC 先开口的对话归招呼系统，玩家主动聊天永远主角先开口。
     */
    greetingTexts?: string[];
}

/**
 * 特殊 NPC 静态定义（= Identity + SocialCopy + 结识初值 + 关联剧本）
 * 竞争型的生涯层（fame/rank）由 SpecialNpcDef.rival 提供初值、开局实例化为 NormalNpc 注入派角池。
 */
export interface NPCDef extends NpcIdentity, NpcSocialCopy {
    /**
     * 初始好感度：V1 统一 -1 = 未结识（不显示于人脉页），结识后由系统置 0 起步。
     * 保留为字段而非硬编码：固定剧情可能让个别 NPC 开局即已结识（≥0）。
     */
    initialFavor: number;
    /** 关联剧本 ID：通告拍摄周偶遇池来源（初遇 VN 选「友好」才结识入页） */
    relatedScripts: string[];
    /** 捏脸立绘配置（固定 NPC 定稿形象，捏脸工坊导出）；缺省 = 维持 emoji 图标兜底 */
    avatar?: AvatarConfig;
    /**
     * 获奖感言（0~1 条）：该 NPC 得年度奖项时，颁奖夜现场版优先念这一句。
     * 可用 %奖项名% / %作品名% / %角色名% / %咖位名%；缺省、空白或占位符取不到值则回落四性格通用池。
     * 文案源与格式见 docs/特殊人脉/模板文案格式.md 第 10 节。
     */
    awardSpeechText?: string;
}

/**
 * 作品履历条目（批6）：玩家与普通 NPC 共用，播出完结（done）时写入。
 * 一行 = 一个人在一部剧里的一个角色位，所以同剧多角色会写多行、scriptId 相同而 roleIndex 不同（外键重复，非数据重复）。
 * 玩家 score = 杀青快照实际评分；NPC score = 剧本 baseScore（定稿：NPC 不掷属性加成）。
 * 微博播出期宣传功能（另一会话）的数据接口 = 本表 + airing 状态合同/在播 job。
 *
 * 展示三字段（workName/roleType/genre）为**写入即定格**的快照：本表只存坐标时，读者必须回查
 * GameData.SCRIPT_MAP，于是剧本表改一个字就会改写玩家第 3 年演过的戏的历史（与微博「发布瞬间定格」同源的问题）。
 * 快照只覆盖「表里的字」；参评档位需要全剧最小番位、热度需要剧宣活账本，两者⛔ 不入快照，
 * 故剧本表仍受两条纪律约束（docs/剧本/导入台账.md）：
 *   ① roles 数组只准末尾追加，⛔ 中间插入/重排（roleIndex 是唯一指针，漂移不可检测）；
 *   ② ⛔ 删整部剧（删了就只剩快照，档位无从派生）。
 */
export interface FilmographyEntry {
    scriptId: string;
    roleIndex: number;
    /** 剧名快照（播完结算定格）：剧本表日后改名不回写历史；剧本已从表中缺失时落空串 */
    workName: string;
    /** 角色定位快照（女主角/男二号/常驻MC…），只作展示与分类，⛔ 不承载参评档位 */
    roleType: string;
    /**
     * 番位快照（1~6+；无番位角色缺省不写）。
     * 2026-10-22 咖位曲线批加：顶流后「主演高分作品」门要判一番/二番，⛔ 再靠 roleType 字符串猜；
     * 旧存档没有本字段 ⇒ `RankSystem.getBestWorkCount()` 回表 `SCRIPT_MAP` 反查（剧本下架则该条不计）。
     */
    billing?: number;
    /** 类型快照：金枢/星汉分池依据；剧本已从表中缺失时落 'other' */
    genre: ScriptGenre;
    /** 实际评分（玩家）/ 基础评分（NPC），2-10 */
    score: number;
    /** 播出完结时的年份 */
    year: number;
    /** 播出完结时的月份 1-12 */
    month: number;
    /**
     * 「爆」标（2026-09-30 用户拍板：结算页代表作/履历展示用，阈值同影视热度榜
     * `workHeat ≥ HEAT_BOOM_LINE(100)`，**入档时定格**——剧下架/表改热度/长尾衰减后仍可显示）。
     * ⚠️ 口径与热度榜**有意分叉**（2026-10-03 用户拍板选 A）：热度榜按当月显示热度动态摘标
     * （语义=「现在爆不爆」），结算页定格（语义=「生涯历史荣誉，当年爆过」）。
     * 可补可选字段，旧履历缺省 = 不显示爆标，⛔ 不因此升存档版本。
     */
    boom?: boolean;
}

// ===== 结局系统（2026-09-29 结局批，设计=docs/实现说明.md §3.4g + 迁存章 A5）=====

/** 结束方式：natural=满 40 岁自动退役 / voluntary=主动退圈（月底决策日，自带确认框）/ stage_fail=阶段审查失败 */
export type EndedBy = 'natural' | 'voluntary' | 'stage_fail';

/** 数字人生统计块（全部为终局一次性聚合，字段来源见设计文档 §4.2 映射表） */
export interface CareerStats {
    /** 生涯作品数（口径 = filmography，即播出完结；杀青未播完即退役的不计） */
    works: number;
    /** 按类型分列（movie/tv/web/short/variety/other） */
    worksByGenre: Partial<Record<ScriptGenre, number>>;
    /** 累计上课节数 = Σ pathCourseCounts */
    courses: number;
    /** 累计发帖条数 / 累计获赞 */
    posts: number;
    likes: number;
    /** 结识业内人数（relationships ≥0）/ 挚友数（≥80） */
    knownNpcs: number;
    closeFriends: number;
    /** 累计赚取金钱 */
    totalMoney: number;
    /** 退役年龄 */
    age: number;
}

/**
     * 周目结算快照（结局批）：endGame 时一次性生成，写入 MetaData.lastCareerSummary。
     * 结算页只读本快照（⛔ 读活内存——删档/刷新安全），MetaData 与玩家存档分离存储，删档不删它；
     * 同时是 P2「回顾室」的地基（当前只存最近一局）。
 */
/**
 * 生涯代表作条目（结局批 2026-09-30 展示强化）：履历快照 + 角色/热度定格——
 * 角色名与作品热度在结算时回表取一次随快照落盘，之后剧本表回改 ⛔ 回写（履历快照哲学）。
 */
export interface TopWorkEntry extends FilmographyEntry {
    /** 角色名（回表快照；综艺等无姓名角色缺省，展示端回退 roleType） */
    roleName?: string;
    /** 作品热度（结算时 workHeatOf 定格：剧本 heat + 剧宣/客串加成） */
    heat: number;
}

export interface CareerSummary {
    endedBy: EndedBy;
    /** 仅 stage_fail：失败阶段 1-4 */
    failStage?: number;
    rank: number;
    rankName: string;
    fame: number;
    fans: number;
    reputation: number;
    /** 基调档位 key（T1~T6，见 Endings.ts）；失败时为 'FAIL' */
    tierId: string;
    /** 形状规则 key（S1~S6）；无命中缺省 */
    shapeId?: string;
    /** 隐藏结局 key（H1~H4）——P0 不产出（层后置），字段位保留 */
    hidden?: string;
    /** 结局标题（已拼好：形状缀词 + 基调标题，或失败标题） */
    title: string;
    /** 生涯总结段（结算时填好数字与尾句，⛔ 结算页不再拼模板） */
    summaryText: string;
    /** 仅 stage_fail：动态建议行（三档定性措辞，⛔ 数字差距句式） */
    adviceText?: string;
    stats: CareerStats;
    /** 奖项计数（won=playerWon 条数，nominated=playerNominated 条数；scripted 开场届天然不计） */
    awards: { won: number; nominated: number };
    /** 结局页「获奖陈列」逐条快照（只收 playerWon；按 year 从先往后）。旧档/无获奖时缺省 */
    honors?: AwardHonor[];
    /** 本周目新解锁成就（endGame 时补判 checkMonthly 捕获） */
    newAchievements: { id: string; name: string; points: number }[];
    /** 新解锁成就点合计 */
    earnedPoints: number;
    /** 本局是否刚好解锁随机池（收尾提示行用：endBy 非 stage_fail 且满 40 且此前未解锁） */
    randomPoolUnlockedNow: boolean;
    /**
     * 生涯代表作前三（2026-09-30 二次拍板，替代自选/单部自动）：代表作系数 =
     * ( min(热度,100)÷10 + 评分 ) ÷ 2 × 番位系数（热度归一到 0~10 与评分同权；
     * 番位系数=现行 FAME 表，无番位 0.5）降序取 3，[0] 即代表作；结算时定格，
     * 剧本表回改热度/番位⛔ 回写（履历快照哲学）；无可回表作品时空数组。
     * 展示强化（同日）：角色名/热度随定格落盘（结算页领奖台展示用）。
     */
    topWorks?: TopWorkEntry[];
    /** 终局全量履历快照（结算页履历节直接渲染，剧下架显示快照名不回表） */
    filmography: FilmographyEntry[];
    /** 结算时间戳（回顾室预留） */
    finishedAt: number;
}

// ===== 年度奖项（金枢奖 / 星汉奖，设计=docs/新增功能-年度奖项与网友评价.md）=====

/** 奖项类别：主角档按性别分男主角/女主角，配角档同理 */
export type AwardCategory = 'bestActor' | 'bestActress' | 'bestSupportingActor' | 'bestSupportingActress';

/** 参评档位：主角型看 roleType（⛔ 不看番位）、配角型看该位自身 billing，实时派生（⛔ 不落 ScriptDef、不落 FilmographyEntry） */
export type AwardTier = 'lead' | 'supporting' | 'none';

/** 一名候选人：总分在提名时算定并冻结进名单，颁奖夜只取首位，不再掷骰 */
export interface AwardNominee {
    /** 'player' 或 npcId */
    performer: string;
    /** 显示名（玩家 = 主角名） */
    name: string;
    gender: 'male' | 'female';
    scriptId: string;
    roleIndex: number;
    /** 角色名快照：固定阵容等无 roleIndex 的候选写入；有值时仪式页优先读它 */
    roleName?: string;
    /** 剧名快照：剧本日后改名/下架也不影响当年字样 */
    workName: string;
    roleType: string;
    /** 表演分：玩家 = 杀青快照实际评分，NPC = 剧本基础分 */
    score: number;
    /** 该作客观热度：玩家侧含剧宣累计（撞顶封顶），NPC 侧 = script.heat */
    heat: number;
    rank: number;
    /** 评选总分 = 表演分 + 热度项 + 评审随机（⛔ 咖位项，09-28 改定）；降序后 [0] 即得主 */
    total: number;
}

/** 一届 × 一个类别的结算结果（往屆回看与复获递减的账本） */
export interface AwardRecord {
    awardId: string;
    edition: number;
    category: AwardCategory;
    /** 颁奖时 player.year（0-based） */
    year: number;
    /** 0 候选 → 该类别空缺（⛔ 不下沉补位、不向前借档） */
    vacant: boolean;
    /** 按总分降序；[0] 为得主；vacant 时为空 */
    nominees: AwardNominee[];
    playerWon: boolean;
    playerNominated: boolean;
    /** 开场剧情届（第 23 届）：得主写死，不吃作品记录、不跑算法、不发收益 */
    scripted?: { name: string; workName: string };
}

/**
     * 结局页「获奖陈列」的一条（2026-10-18 加）：endGame 时从 `awardHistory` 快照进 CareerSummary，
     * 删档/刷新安全。角色名一并快照——剧本下架/改名后不从剧本表反查，展示稳定。
 */
export interface AwardHonor {
    awardId: string;
    awardName: string;
    categoryName: string;
    edition: number;
    workName: string;
    /** 角色名（剧本表取不到时缺省＝只显示作品名） */
    roleName?: string;
    /** 获奖时的 player.year（0-based）；陈列按它从先往后排 */
    year: number;
}

/** 提名名单（12 月末产出、次年 1 月颁奖时消费）：随机已在提名时定格 */
export interface PendingCeremony {
    awardId: string;
    edition: number;
    lists: Partial<Record<AwardCategory, AwardNominee[]>>;
}

/** 普通 NPC 在途工作（批6）：与玩家合同同款两阶段（拍摄→播出），月末统一推进 */
export interface NpcJob {
    scriptId: string;
    roleIndex: number;
    /** 'shooting'=拍摄期 / 'airing'=播出期（名气滴灌中） */
    phase: 'shooting' | 'airing';
    /** 剩余拍摄周数（每月末 −4；≤0 → 杀青进入播出期） */
    shootWeeksLeft: number;
    /** 播出期总月数（接角时按剧本快照；播出期从杀青次月起算） */
    airMonths: number;
    /** 剩余名气发放月数（初值=airMonths+1，杀青当月月末滴首月，归零=播出期末月完结移出在途） */
    airMonthsLeft: number;
    /** 本角名气总额 = heat × 番位系数（接角时快照，播出期滴灌） */
    fameTotal: number;
    /** 已滴灌名气（末月发余数 = fameTotal − famePaid） */
    famePaid: number;
    /**
     * 开拍月（2026-10-27 滚动排期批，year*12+month 口径）：收口夜/签约夜建 job 时快照 shootStartAbsOf，
     * advanceMonthJobs 在开机月前不递减——全剧组同月开机同月杀青。旧档 job 缺省 ⇒ 无闸（旧钟跑完）。
     */
    shootStartAbs?: number;
}

/** 普通 NPC 定义（开局随机生成，与玩家竞争剧本角色） */
/**
 * 随机 NPC（运行时实例）：Identity + CareerState + 性格模板 key。
 * 竞争型特殊 NPC 开局实例化后也是本形状（注入派角池），SocialSystem/EncounterSystem 统一处理。
 */
export interface NormalNpc extends NpcIdentity, NpcCareerState {
    /** 性格模板 key（对应 PERSONALITY_TEMPLATES）——文案来源：随机 NPC 走共享模板池，特殊 NPC 走 NPCDef 专属文案 */
    personality: string;
    /** 捏脸立绘配置（67 批：开局生成 NPC 时同步随机生成；旧档缺此字段 = 显示回落 emoji） */
    avatar?: AvatarConfig;
}

/** 成就定义 */
export interface AchievementDef {
    id: string;               // ACH_101
    name: string;
    description: string;
    /**
 * 触发条件类型（月末 checkMonthly 统一判定；除 event 外全部纯读现有数据字段，无新落盘）：
 *  rank 咖位 / attribute 指定属性(≥) / anyAttr 七维最大值 / allAttr 七维全部≥ /
 *  money 累计赚取 / shoots 累计杀青 / works 播完作品数 / score 单部最高评分 /
 *  avgScore 复合「均分,最少部数」/ awards 获奖次数(both=金枢+星汉各≥1) / nominations 提名次数 /
 *  fans 粉丝 / likes 累计获赞 / courses Σ训练次数 / friends 挚友数 / known 结识数 /
 *  guestOut 请好友客串人次 / guestIn 被邀客串接受次数 /
 *  rankLowRep 复合「咖位下限,口碑上限」/ rankNoPosts 复合「咖位下限」且从未发帖 /
 *  shootsNoAwards 复合「杀青≥v 且获奖为 0」（后三个为隐藏彩蛋专用）/
 *  event flag / tag 标签 ID（逗号分隔 AND 组合）
     */
    conditionType: 'rank' | 'attribute' | 'anyAttr' | 'allAttr' | 'money' | 'shoots' | 'works'
        | 'score' | 'avgScore' | 'awards' | 'nominations' | 'fans' | 'likes' | 'courses'
        | 'friends' | 'known' | 'guestOut' | 'guestIn' | 'rankLowRep' | 'rankNoPosts'
        | 'shootsNoAwards' | 'event' | 'tag';
    /** 触发条件值；avgScore / rankLowRep 用「a,b」复合串 */
    conditionValue: number | string;
    /** 触发条件补充：attribute 的属性 key；awards 的领域（'both'=金枢+星汉各≥1） */
    conditionAttr?: AttributeKey | 'both';
    points: number;           // 成就点数
    isHidden: boolean;        // 是否隐藏
    /** 成就商店面板分组（缺省归 milestone） */
    category?: AchievementCategory;
}

/** 成就面板分组（薄荷块顺序固定，块内按表序） */
export type AchievementCategory =
    | 'milestone' | 'rank' | 'attribute' | 'money' | 'works' | 'score'
    | 'awards' | 'fans' | 'social' | 'training' | 'endings' | 'hidden';

/** 行程定义 */
export interface ScheduleDef {
    id: string;               // SCHEDULE_001
    name: string;
    category: 'course' | 'work' | 'rest'; // 课程/工作/休息
    /** 属性路径（仅训练有）：颜值/演技/气质/体能/情商，无口碑课；特殊训练无 path */
    path?: AttributeKey;
    /** 路径内等级 1-3（仅路径训练：初级→中级→高级，替换规则用；特殊训练/工作无此字段） */
    level?: number;
    /** 工种路径（仅工作有）：同工种内高档解锁后替换低档 */
    trade?: 'performance' | 'shoot' | 'event' | 'stage' | 'live' | 'music';
    /** 消耗：体力 */
    energyCost: number;
    /** 消耗：金钱 */
    moneyCost: number;
    /** 产出：属性提升（永远不含 reputation） */
    attributeGain: Partial<AttributeSet>;
    /** 产出：金钱 */
    moneyGain: number;
    /** 产出：体力恢复 */
    energyRestore: number;
    /** 产出：名气（工作名气收益） */
    fameGain?: number;
    /** 仅工作：咖位门槛（训练升级与咖位无关，一律不填） */
    requireRank?: number;
    /**
     * 解锁/升级所需路径累计次数：key=AttributeKey（训练路径），value=所需累计次数。
     * 训练升级也用它表达（如中级训练 { talent: 12 }、高级训练 { talent: 30 }）；
     * 特殊训练/工作可填多个 key（需同时满足才解锁）。
     */
    requirePathCounts?: Partial<Record<AttributeKey, number>>;
    /** 同路径/同工种替代的上一档 ID（保持链式结构） */
    replaces?: string;
    /** 行程介绍（决策页详情卡展示专用）：短句定义式文案，与执行页旁白的 description 分离 */
    intro: string;
    /** 行程文案（叙事描述，月度执行页旁白用） */
    description?: string;
}

/** 行程格位统一对象（每周一格） */
export interface ScheduleSlot {
    kind: 'course' | 'work' | 'rest' | 'audition';
    refId: string;          // ScheduleDef.id（course/work/rest）或 ScriptDef.id（audition）
    contractId?: string;    // 仅 kind='work' 且为签约通告：关联 contracts 里的合同ID
    endorseId?: string;     // 仅 kind='work' 且为品牌活动周：关联 endorsements 里的合同ID（2026-11-02）
    title?: string;         // 展示用快照（如"《夏日的回信》第2周"），存档冗余便于渲染
}

/**
 * 签约通告（合同）——批6 生命周期三段：
 * active（拍摄中）→ airing（播出中，名气按月滴灌）→ done（播完，口碑/filmography 结算）。
 * 片酬与属性成长仍在杀青（active 结束）时一次结；评分取杀青快照，口碑在播出完结时落。
 * 2026-09-28 戏份结算批：「杀青」= 排完**自己的戏份周数**（不陪剧组耗满档期），airing 即转入；
 * 名气滴灌另有剧级档期门控（advanceAiringContracts：未到签约月+剧级档期不滴不倒计时，同剧同开播）。
 *
 * 2026-10-25 直签批加 status='pending'：免试镜直签的**待签约**档（经纪人帮谈/片方递本掷中时生成、
 * 玩家在人脉页经纪人消息里自己决定签不签）。pending 的语义边界：不占 MAX_PENDING_SCRIPTS 通告槽、
 * 不参与档期违约（checkScheduleBreach 只查 active——offer 挂到玩家处理为止，不倒计时）、不计入任何
 * 履历/奖项/剧宣口径（那些都 filter active/airing）；signedMonth 在 pending 期＝发出月，**签约时重写**。
 * 生命周期：DirectSignSystem 派发（生成）→ 签约（→active，走 signDirectOffer）或放弃（删除+flag）
 * 或市场换新下架（refreshMarket 修剪）。旧档没有 pending ⇒ 可选档不升 SAVE_VERSION。
 */
// ===== 商务代言（2026-10-30 拍板，设计 = docs/玩法设计.md §六「商务代言」）=====

/** 品牌档五档（微商/潮流/新消费/国民/高奢；递约带、预算系数、解约条款随档不同） */
export type BrandTier = 'ws' | 'cl' | 'xf' | 'gm' | 'gs';
/** 品类八种（美妆护肤/服饰/食品饮料/3C数码/汽车/游戏互联网/珠宝高奢/母婴家居） */
export type BrandCategory = 'mei' | 'chuan' | 'shi' | 'shu' | 'che' | 'you' | 'bao' | 'jia';
/** 合作 title：挚友（单次即结）/大使（考察期）/代言人——挚友/大使=高奢专属，其余档恒代言人 */
export type EndorseTitle = 'friend' | 'ambassador' | 'spokesman';

/** 品牌池条目（数据 = docs/品牌文案填写表.md，导码键 = id；文案列由填写表维护） */
export interface BrandDef {
    id: string;               // 'BRD-{档位码}{两位序号}'，导码后不得改
    name: string;
    tier: BrandTier;
    category: BrandCategory;
    /** 调性简介（≤20 字，内部风味档/助记；⛔ 不进 UI，见 Brands.ts 头注释） */
    tagline: string;
}

/**
 * 品牌递约（待签，player.pendingBrandOffer 至多挂 1 个）。送达链 = 经纪人 VN 说事 → 签约弹窗·重档，
 * 两键强制二选一：签约=accept / 放弃=reject（该品牌当周目不再递，brandRejected 记账）。
 * 报价已在递约时算死（基准×品牌系数×名气系数×PAY_SCALE），弹窗与签约共用同一 fee，不二次算。
 */
export interface PendingBrandOffer {
    brandId: string;
    title: EndorseTitle;
    /** 终值报价：挚友=单次活动费用（活动完成后结），长约=年费（5:5 分期：签约头款 50%、约满尾款 50%） */
    fee: number;
    /** 长约年限（挚友=0；大使=1、代言人=1~2） */
    years: number;
    /** 递约绝对月（year*12+month） */
    month: number;
    /** 经纪人 VN 段台词（档位姿态载体，来自品牌填写表 §三 模板+插值） */
    brokerLine: string;
}

/**
 * 商务合同（挚友=单次活动约，签约当月内排一周完成、完成即结；长约=5:5 分期）。
 * 解约三条：名气连续 6 月低于品牌要求 / 口碑档跌至 T2 及以下、挂黑料（高奢·国民）→ 品牌解约+违约金；
 * 玩家主动解约同违约金。品牌解约与到期走月结（monthlySettle），通知进待通报队列由 UI 消费。
 */
export interface EndorsementContract {
    id: string;               // 'ED_{brandId}_{abs}'
    brandId: string;
    title: EndorseTitle;
    tier: BrandTier;
    category: BrandCategory;
    /** 签约绝对月 */
    signedAbs: number;
    /** 签约时咖位（续约跳档惩罚用：续约判定时算「签约后升了几级咖」，2026-10-30 茄子令公式） */
    signedRank: number;
    /** 剩余月数（月结递减，归 0 = 约满自然结束、无违约金）；挚友活动约=0 */
    monthsLeft: number;
    /** 年费（×1 相对口径；长约报酬 5:5 分期——签约结头款 50%、约满结尾款 50%）；挚友=单次活动费用（×1） */
    annualFeeBase: number;
    /** 在约中（解约/约满置 false，行保留供实绩与回看；实绩计数在 endorseTotal 另算） */
    active: boolean;
    /** 挚友专有：活动期限月（=签约当月；玩家须在当月内排一周完成，逾期违约，2026-11-02 茄子令） */
    eventMonth?: number;
    /** 挚友专有：活动费用终值（×PAY_SCALE 已乘，活动完成时到账） */
    eventFee?: number;
    /** 掉出要求咖位线的起点月（回线清除；连续 ≥ 宽限月 → 解约） */
    lowSinceAbs?: number;
}

/**
 * 宣传特访待签邀约（2026-10-31 批；player.pendingVarietyOffer 至多挂 1 个）。
 * 触发=玩家以**一番**身份的剧集杀青（ScriptSystem.completeShoot）**掷骰递约**：递约率=基础 20%
 * × 质量（baseScore 7→×1.5、8→×2）× 正剧负向（×0.5），clamp 90%；战争/谍战/历史直接不上。
 * 选形态（2026-10-31 茄子定稿）：掷中后从资格池随机挑——四档综艺（粉丝向→咖位 ≤6 开麦/7+ 星动，
 * 大驾收一切、追更仅网剧）+路演（仅电影）+媒体专访（仅偏正剧向=非粉丝向）。同节目冷却 12 个月
 * （上过不重复递，放弃不算）。放弃=不拉黑（机会型，下部剧杀青还会来）。
 */
export interface PendingVarietyOffer {
    /** 宣传形态 id：SCRIPT_170/171/172/174（综艺档，variety.ts 已导码）或 PROMO_ROADSHOW/PROMO_INTERVIEW（路演/专访） */
    showId: string;
    /** 被宣传的作品（触发杀青的那部一番剧）——结算时作品 heat 挂它的 heatPromoted 账 */
    scriptId: string;
    /** 通告费**终值**（GameConfig.VARIETY_PROMO 按节目档锁定，⛔ 不乘 PAY_SCALE） */
    fee: number;
    /** 待播宣传月（杀青次月=开播月，dramaTimelineOf.premiere 口径；拖期兜底 max(开播月,下月)） */
    eventMonth: number;
    /** 录制周（1~4）：综艺档递约时定死；路演/专访不带周（助理通知→玩家选周落定） */
    eventWeek?: number;
    /** 递约绝对月（year*12+month） */
    month: number;
    /** 递约台词：综艺=经纪人口吻、路演/专访=小助理口吻（载体随形态走） */
    brokerLine: string;
}

/**
 * 已签的综艺录制通告（player.varietyGuests；挚友品牌活动同模型）：
 * 占自由日周（EndorsementSystem.occupiedEventDetail 同尺互斥）、到周自动赴约 VN（呈现层），
 * 录制月月结自动结算=通告费到账 + 作品 heat +10/15/20（入 heatPromoted 账本，同池封顶在读取端）
 * + 名气 +5/8/12。无续约、无官宣（非品牌合作）。
 */
export interface VarietyGuestBooking {
    id: string;               // `VG_{showId}_{abs}`
    /** 宣传形态 id（同 PendingVarietyOffer.showId：SCRIPT_* 综艺档或 PROMO_ROADSHOW/PROMO_INTERVIEW） */
    showId: string;
    scriptId: string;
    eventMonth: number;
    eventWeek: number;
    /** 通告费终值（签约时从 GameConfig.VARIETY_PROMO 锁定；路演/专访=0） */
    fee: number;
    /** 在约中（录制月月结完成置 false，行保留供回看） */
    active: boolean;
}

/**
 * 待弹的品牌官宣（2026-10-30 茄子令：官宣不再发微博面板信息流，改 media 微博弹窗，
 * 在**玩家点「确认行程」之后、月度执行之前**的决策页弹出——挂确认行程链 `app.playAnnounceThen`，
 * 与特殊剧情 media 同一只 MediaPanel）。
 * 签约当刻入队（absMonth=签约月），`consumeAnnounce` 放行 absMonth ≤ 当前月（日期只做脏数据护栏，
 * 真正的时机由挂点保证）；多条按入队顺序逐条弹。
 */
export interface PendingBrandAnnounce {
    brandId: string;
    title: EndorseTitle;
    /** 签约绝对月（year*12+month）；≤ 当前月才放行（护栏） */
    absMonth: number;
}

export interface Contract {
    id: string;               // 合同唯一ID（存档内）
    scriptId: string;         // 引用 ScriptDef.id
    roleIndex: number;        // 该合同对应的角色在 script.roles 中的索引
    /** pending=直签待签约（见接口头注释）/ active=拍摄中 / airing=播出中 / done=已播完结 */
    status: 'pending' | 'active' | 'airing' | 'done';
    /**
     * 本角色**要拍的拍摄周数**（签约时快照自 role.shootWeeks）：拍满即杀青转 airing。
     * 2026-10-27 日历化批：本值不再是「递减计数器」，只是应拍总量。
     * （旧字段 remainingWeeks 已删：扣周模型与剧组日历是两套钟，两者的同步胶水即 parallelShootWeek，
     *   而那条胶水会漏掉特殊剧本的 cast/done 钩子——整段机制一起删掉后该 bug 不复存在。）
     */
    shotWeeks: number;
    /**
     * **已拍的拍摄周数**（2026-10-27 日历化批）：唯一写入口 = `ScriptSystem.shootOneWeek`。
     * 记在合同上而不是另开一张玩家侧表：合同删除（违约/解约/换新修剪）时进度自动跟着走，
     * 不需要额外的清键逻辑，也不会出现「键还在、合同没了」的脏记账。
     * 只有**真拍**才 +1——体力不足被 forceRest 换掉的那一周不计（不许靠体力耗尽免掉拍摄）。
     */
    shotWeeksDone?: number;
    /** 签约时的绝对月份（year*12+month）。2026-10-27 滚动排期起只作记录——时点锚一律走 shootStartAbs；旧存档必有 */
    signedMonth: number;
    /**
     * 开拍月（year*12+month 口径）：普通剧=入池月+POOL_MONTHS、特殊剧=定角夜次月、
     * 新手专属剧=签约次月（`shootStartAbsOf` 唯一推导口）。开机前合同为「待开机」态（行程置灰不可排）。
     * 2026-10-27 日历化批起**必填**：老档已整体作废（SAVE_VERSION v16），不再有「缺省回退 signedMonth」
     * 这一路——四个日子（开机/杀青/开播/违约线）一律由它算术推导，见 `dramaTimelineOf`。
     */
    shootStartAbs: number;
    /** 同剧组同事（签约时绑定的普通 NPC ID，拍摄周偶遇池据此；已排除玩家本人饰演的角色） */
    castNpcIds?: string[];
    // 2026-10-16 茄子令撤掉「每剧首拍周必遇保底」，此处的 `encounterGranted?: boolean` 旗标随之删除。
    // 存档不升版：老档里那个键读回来没人读，随下次写盘自然消失。
    /**
     * 直签单的叙事包装（2026-11-03 茄子拍板「按乙改」）：`broker`=经纪人帮谈、`pitch`=片方递本，
     * 唯一消费者＝经纪人那句 VN 台词（`DirectSignSystem.speechOf`），签约/数值/弹窗一律不看它。
     * 记在合同上而不是只记进程内的表：读档回来口吻不许变。老档没有这个键时按番位回推一次
     * （一二番=递本、其余=帮谈），下一次写盘就带上字段了。不升 `SAVE_VERSION`（可选字段先例＝`pendingBrandOffer`／`heatPromoted`）。
     */
    directKind?: 'broker' | 'pitch';

    // ===== 播出期字段（批6；杀青进入 airing 时写入，快照自 ScriptDef） =====
    /** 杀青时算出的实际评分快照（播出完结时据此结算口碑，不随后续属性漂移） */
    scoreSnapshot?: number;
    /** 杀青时算出的本角色名气总额 = heat × 番位系数（播出期按月滴灌的总量） */
    fameTotal?: number;
    /** 已滴灌发放的名气（末月清零头：本月滴灌 = fameTotal − famePaid） */
    famePaid?: number;
    /** 播出期总月数（airMonths ?? AIR_MONTHS_BY_GENRE[genre]，杀青时快照；播出期从杀青次月起算） */
    airMonths?: number;
    /**
     * 剩余名气发放月数（每月末 −1，归零=播出期末月 → done）。
     * 初值 = airMonths + 1（批6 修订：滴灌自杀青当月月末开始，1 个月播出的剧分 2 个月末发完）。
     * 2026-09-28 戏份结算批：未到剧级档期（签约月+⌈lead shootWeeks/4⌉）时门控跳过，本值不推进。
     */
    airMonthsLeft?: number;
    /**
     * 「开播」播报的一次性标记（2026-10-03 治本：同一部剧隔月报两次）。
     * 旧判据 `airMonthsLeft ≥ airMonths + 1` 是**推**出来的，`airMonths` 一漂移就退化成连月各报一次
     * ⇒ 事件要记在合同上，⛔ 依赖算术单调。可选字段、缺省=没报过 ⇒ ⛔ 升 SAVE_VERSION。
     */
    premiered?: boolean;
}

/** 试镜成功待签约的角色记录 */
export interface AuditionQualified {
    scriptId: string;         // 引用 ScriptDef.id
    roleIndex: number;        // 角色在 script.roles 中的索引
}

/**
 * 月末结转包（2026-11-03 竞角重排批）：收口夜这一晚跨一次存盘的两件事——
 *  ① `rows` 竞角队列行（玩家表态后逐行摘掉）：读档后原样再弹，掷出的成败跟着存档走，不重掷；
 *  ② `closingSlate`／`closingAbs` 收口夜的剧单与当月：乙段派角按这一晚推开机月，不随换月顺延。
 *  派角等玩家表态之后才做（收口夜不先派给 NPC），见 GameState.dispatchClosingRoles。
 */
export interface MonthHandover {
    rows: { scriptId: string; roleIndex: number }[];
    /** 收口夜当月（year*12+month）：乙段派角的推算锚 */
    closingAbs: number;
    /** 收口夜的剧单（乙段派角的池） */
    closingSlate: string[];
}

/** 经纪人月度通知（页面 5b 聊天页历史通知） */
export interface ManagerNotice {
    /**
     * 类型：脱粉惩罚 / 咖位晋升 / 带资进组解锁 / 通告超时作废 / 训练升级 / 工作解锁
     * ＋ directSign＝直签戏约（2026-10-25 批：唯一重新入 `managerNotices` 档的新通知——
     * NoticeSystem 第七十四批「⛔ 再入档」改判针对 NoticeSystem.push 通道，直签由
     * DirectSignSystem 直接入档，见其类头注释）
     * ＋ promoBought / promoBackfire / prCleared＝经纪人工作台回执（2026-10-27 批：买通稿/危机公关
     * 的即时结果，玩家在经纪人页主动触发，回执入档供当月回看；纯文本气泡，无挂块）
     */
    kind: 'deroof' | 'rankUp' | 'buyinUnlock' | 'studioUnlock' | 'deadline' | 'courseUpgrade' | 'workUnlock' | 'directSign' | 'promoBought' | 'promoBackfire' | 'prCleared';
    /** 发生的绝对月份（year*12+month） */
    month: number;
    /** 通知正文 */
    text: string;
    /**
     * 直签戏约关联的合同 ID（仅 kind='directSign'）：人脉页据此在通知气泡下挂合同块。
     * 消息**当月有效**（2026-10-25 茄子令）：签/放弃后保留当月回看（已签约/已放弃态），
     * 下月月初统一清（rollMonthly 第一步，处理与否都清——比招呼的「已回应跨月留」更短）。
     */
    offerId?: string;
    /**
     * 直签戏约的角色坐标快照（scriptId+roleIndex，仅 kind='directSign'）：放弃后合同从存档
     * 删除，当月回看的「已放弃」合同块仍按放弃前原内容渲染（红条+胶囊，2026-10-25 茄子令），
     * 坐标在就能回查 SCRIPT_MAP（全量表）。dispatch 时写入；decline 删合同前回写兜旧档。
     */
    scriptId?: string;
    roleIndex?: number;
}

/** NPC 主动打招呼消息（月初掷概率产出，方案见 docs/玩法设计.md §八 + 实现说明迁存章 A3）：
 *  存于 PlayerState.greetings，聊天页按 NPC 回放；read 驱动人脉页红点，replied 驱动「回应」按钮 */
export interface NpcGreeting {
    npcId: string;
    /** NPC 招呼语（按性格模板随机） */
    text: string;
    /** 产生时的年份索引（显示 +1） */
    year: number;
    /** 产生时的月份 1-12 */
    month: number;
    /** 玩家是否进过该 NPC 聊天页（红点清除） */
    read: boolean;
    /** 玩家是否已回应（+1 好感已发，不占每月聊天额度） */
    replied: boolean;
    /** 招呼的快捷回复语（roll 产出时即从 GREETING_REPLY_ME 定好，聊天页按钮直接显这句、点击发这句，保证所见=所发） */
    replyText?: string;
    /** 特殊 NPC 手写招呼的 NPC 再回复（greetingTexts 用 | 分三段时第三段；缺省=走通用池 GREETING_REPLY_NPC） */
    replyNpcText?: string;
    /** 队列条目种类（好友客串批①）：缺省视为 'greet' 招呼；'guestInvite'=带选项块的客串邀请 */
    kind?: 'greet' | 'guestInvite';
    /** 邀请选项文案（产出时 roll 定；约定下标 0=接受 / 1=婉拒，效果语义在批③结算） */
    inviteOptions?: string[];
    /** 已选邀请的下标（undefined=未抉择；选定即定格，兼作重复点击的幂等闸） */
    picked?: number;
    /** 选定后的定格气泡（[玩家回话, NPC 反应]），供历史回放；招呼的回应交换是会话级不落盘，邀请按方案档要求落盘（⛔ 刷新换台词） */
    replyTexts?: string[];
    /** 方向一邀请：好友在拍剧的 scriptId（结算/展示上下文，产出定格；招呼条目无此字段） */
    guestScriptId?: string;
    /** 方向一邀请：好友剧的静态底数 heat（接受时 fame=max(1,round(本值×GUEST_RIDE_K))，产出定格⛔ 结算再回表） */
    guestHeat?: number;
}

/** 小助理播报消息（对话框弹出内容；历史在 PlayerState 内，示例见 AssistantSystem） */export interface AssistantMessage {
    kind: 'deroof' | 'rankUp' | 'buyinUnlock' | 'deadline' | 'award' | 'tut';
    /** 绝对月份（year*12+month） */
    month: number;
    /** 播报文案 */
    text: string;
}

/** 微博发布记录（存档冗余：展示用文案快照） */
export interface PostRecord {
    /** 发布时的绝对月份（year*12+month，滚动窗口判断用） */
    month: number;
    /** 发布类型：营业 / 工作 / 公益（剧宣批重排，⛔ 旧「日常」档随 v11 拒载） */
    type: string;
    /** 发布时的实际文案（展示用，不再按月份伪随机） */
    text: string;
    /** 剧宣帖：所宣剧本 id（同剧每月限 1 条查重 + 热度账本归属） */
    scriptId?: string;
    /**
     * 非本人署名的发布者（商务代言批 2026-10-30：品牌官宣微博=品牌官微名义入玩家信息流，如「斐珞」）。
     * 缺省=玩家本人。展示端（WeiboPanel）渲染署名行，未接前缺省行为不变。
     */
    author?: string;
    /** likes/reposts：发布瞬间按收益正比算一次落盘，永不再变（⛔ 读取端现算——旧月份伪公式已删） */
    likes: number;
    reposts: number;
}

/** 阶段审查定义 */
export interface StageDef {
    id: number;               // 阶段编号 1-4
    startAge: number;         // 起始年龄
    endAge: number;           // 结束年龄
    /** 审查要求：达到的咖位等级 */
    requiredRank: number;
    description: string;
    /**
     * 审查未通过时先演的剧情事件（2026-10-21 茄子令「失败要有对话，不能突然结局」）。
     * 配了它，`GameLoop.checkStagePass` 只派发事件并落「待演」标记，终局等演出排干后
     * 由 `GameLoop.resolvePendingStageFail` 落；没配的阶段仍按旧路当场结算。
     */
    failEvent?: string;
}